// Kuala Lumpur, "Twin towers over Lake Symphony": a warm, wet tropical night in KLCC Park.
// Where we stand: on the south-east bank of Lake Symphony in KLCC Park (3.156 N, 101.713 E), looking north-west
// (bearing about 315 deg) across the lake at the Petronas Twin Towers rising behind the Suria KLCC mall's lake front.
// Menara KL stands on the forest of Bukit Nanas to the left (really west-south-west, 69 deg left and 1.1 km off: its
// distance is cheated so it stands near the towers, smaller, on its hill). Back to front: deep blue sky, big moon,
// stars, wisps -> hazy far skyline -> Bukit Nanas with Menara KL -> the towers around the twins (a hotel on the left, a
// round tower and a glass tower on the right) -> the Petronas Twin Towers (fluted steel shafts, the double-deck
// skybridge on its inverted-V legs, the setback crowns narrowing faster towards the top, the pinnacles with their balls
// and rings) -> Suria KLCC's lit lake front and esplanade -> Lake Symphony (the mall and the towers mirrored, the
// fountain line) -> the wet granite path along the lake, park lamps, a rain tree and palms -> the park's red jogging
// track and its planting -> a 24-hour mamak on the left (cheated over from Jalan Ampang, where such places face the
// towers): fluorescent tubes, the roti griddle, the teh tarik pull, plastic stools -> tropical drizzle at three
// depths. A rooftop billboard stands on the mall's roof right of the towers.
//
// The loop's timeline (seconds)                                                    sound
//   6-24     a plane crosses high, west to east
//   20-86    Lake Symphony's first show: bubblers and mist (20-28), the candles rise from the middle out
//            (28-40), the swell, the alternating pairs and the crossing arcs in white, cyan, rose and violet
//            (40-70), the tall finale (70-78), falling back to foam (78-86)              splash 74
//   100-140  Menara KL's colour wash: green, blue, violet, pink, back to its own white and blue
//   124      a goal on the mamak's TV: the diners' arms go up, the tea pull pauses       chime 124
//   136-154  a plane crosses high, east to west
//   150-196  the second show, shorter, warm, violet and cyan                             splash 188
//   200-232  a warm shower: the drizzle thickens, umbrellas go up on the path, two soft flashes behind
//            Bukit Nanas at 209.5 and 222.4 (thunder a second later)                   thunder 210.5, 223.4
//   always   drizzle at three depths, the lake's mirror and streaks, windows, aviation lamps, the towers'
//            sparkle, wisps, walkers and joggers, the tea pull every 6 s and the roti spun every 8 s, steam,
//            the fan and the TV, laundry, mynas and the rain tree in the breeze
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, glyph, FONT3, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
// The twin towers (real: 451.9 m to the tips, 170 m to the skybridge, a 46.4 m spire; photographed from the lake the
// shaft is 1/7.7 of the height, the straight shaft 0.55 of it, the setback crown 0.34 and ball and spire 0.11)
const TOW = { l: 233, r: 275, hw: 11, tip: 8, base: 175 };   // shaft centres, shaft half-width, spire tip, the (hidden) foot
const NEEDLE = [8, 21], BALL = [22, 25], CROWN_TOP = 26, SHAFT_TOP = 83;
// the setback crown from the shaft up: [rows, half-width]; the tiers get shorter and the steps quicker towards the top
const TIERS = [[14, 10], [12, 9], [5, 8], [4, 7], [7, 6], [5, 5], [4, 4], [3, 3], [3, 2]];
const BRIDGE = { y0: 110, y1: 114, x0: TOW.l + TOW.hw + 1, x1: TOW.r - TOW.hw - 1, leg: 132 };   // skybridge rows, span, where its legs meet the towers
const MKL = { x: 160, tip: 36, pod: 58, shaft: 78, base: 146 };   // Menara KL: antenna tip, top of the pod, top of the shaft
const MALL = { x0: 124, x1: 436, roof: 152, atrium: 146 };      // Suria KLCC's lake front
const SHORE = 175;                  // the esplanade's lip = the lake's far edge
const KERB = 199;                   // the near granite kerb of the lake
const FEET = 206;                   // the row the people on the lakeside path stand on
const MAMAK = { x1: 110, roof: 74, sign: 133, ceil: 144, floor: 205 };
const BB = { x: 296, y: 127, w: 48, h: 20 };                  // billboard face on the mall's roof (the pitch is painted here)
const MOON = { x: 326, y: 30, r: 6 };
const TREE = { x: 452, foot: 214 };                           // the rain tree's trunk
const LAMPS = [{ x: 150, y: 181 }, { x: 362, y: 181 }];       // park lamps on the lakeside path (lantern centres)
const TRACK = 236;                  // the jogging track's top edge at its middle (it bows towards us at its ends)

/* ================= The loop's timeline (see the table above) ================= */
const SHOWS = [
  { a: 20, rise: 28, dance: 40, fin: 70, fall: 78, b: 86, tints: ['white', 'cyan', 'rose', 'violet'] },
  { a: 150, rise: 156, dance: 164, fin: 184, fall: 190, b: 196, tints: ['warm', 'violet', 'cyan'] },
];
const WASH = [100, 140];            // Menara KL's colour wash
const GOAL = 124;                   // the goal on the mamak's TV
const SHOWER = [200, 232];          // the warm shower
const BOLTS = [{ t: 209.5, x: 132 }, { t: 222.4, x: 196 }];   // soft flashes behind Bukit Nanas
const PLANES = [{ a: 6, b: 24, x0: -12, x1: 492, y0: 44, y1: 34 }, { a: 136, b: 154, x0: 492, x1: -12, y0: 24, y1: 40 }];
const TARIK_EVERY = 6, ROTI_EVERY = 8;

/* ================= Pixel helpers ================= */
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    if (a >= 255) { d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = 255; return; }
    const k = a / 255, ok = d[o + 3] / 255, oa = k + ok * (1 - k);
    for (let j = 0; j < 3; j++) d[o + j] = Math.round((rgb[j] * k + d[o + j] * ok * (1 - k)) / (oa || 1));
    d[o + 3] = Math.round(oa * 255);
  };
  const alpha = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const rectP = (x0, y0, rw, rh, col) => { for (let y = y0; y < y0 + rh; y++) for (let i = x0; i < x0 + rw; i++) put(i, y, col); };
  return { c, x, put, alpha, rect: rectP, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// integer Bresenham line through a put(x, y) function
function lineP(put, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    put(x0, y0, col);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (MIXED[k]) return MIXED[k];
  const A = hex(a), B = hex(b);
  return (MIXED[k] = '#' + A.map((v, i) => Math.round(lerp(v, B[i], clamp(t, 0, 1))).toString(16).padStart(2, '0')).join(''));
}
// a ramp value v (0..1) picked by ordered dither at pixel (i, j)
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
// The signs' own M, W, N, K and V, wider than FONT3's, which read as H or Π at 1x (after captionGlyph in ui/postcard.js)
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
};
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s.toUpperCase()].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + 1, 0) - (s ? 1 : 0);
/** sign text with the wide letters, into a painter (p.put) or a context; returns the width */
function signText(put, s, x, y, col) {
  let cx = x;
  for (const ch of s.toUpperCase()) {
    const g = glyphOf(ch);
    if (g) g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') put(cx + i, y + j, col); });
    cx += (g ? g[0].length : 3) + 1;
  }
  return cx - x - 1;
}

/* ================= Sky: deep tropical blue, a warm humid haze over the city, a big moon ================= */
const SKY = ['#030716', '#050b1d', '#071026', '#0a1530', '#0d1b3a', '#112245', '#162a50', '#1c3159', '#25385f',
  '#313d63', '#3f4265', '#4e4664', '#5e4b62', '#6e505e'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 178), 1.45) * 0.86;
    v += Math.max(0, 1 - Math.hypot((i - 250) / 260, (y - 182) / 92)) * 0.2;    // the city's sodium glow, warmest under the towers
    return Math.max(0, v);
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const craters = [[-2, -1, 1.6], [2, 2, 1.3], [1, -3, 0.9], [-3, 3, 0.8], [3, -1, 0.7]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -4 ? '#fffaea' : '#f6ecd2';
    if (dd > r - 1.1) col = dx + dy > 0 ? '#c8bc9e' : '#e8dcbc';
    for (const [cx, cy, cr] of craters) if (Math.hypot(dx - cx, dy - cy) < cr) col = dx + dy > 2 ? '#cfc4a6' : '#ddd2b4';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{ const r = rng(23); for (let i = 0; i < 46; i++) {
  const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 100);
  if (Math.hypot(x - MOON.x, y - MOON.y) < 16) continue;
  STARS.push({ x, y, k: 18 + Math.floor(r() * 50), ph: r(), b: 0.25 + r() * 0.55 * (1 - y / 140) });
} }
// three thin wisps lit from below by the city: they drift one or two screen widths a loop
const WISPS = [{ seed: 14, y: 6, w: 150, h: 10, k: 1 }, { seed: 33, y: 21, w: 140, h: 10, k: 2 }, { seed: 3, y: 41, w: 170, h: 12, k: 1 }, { seed: 9, y: 59, w: 180, h: 11, k: 2 },
  { seed: 21, y: 78, w: 170, h: 12, k: 2 }, { seed: 27, y: 96, w: 160, h: 10, k: 1 }];
function renderWisp(def) {
  const r = rng(def.seed), p = painter(W, def.h + 2), x0 = Math.floor(r() * W), puffs = [];
  for (let k = 0; k < 9; k++) puffs.push({ x: x0 + (r() - 0.5) * def.w, y: def.h * (0.3 + r() * 0.45), rx: 10 + r() * 24, ry: 1.8 + r() * def.h * 0.22 });
  for (let y = 0; y < def.h + 2; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      v = Math.max(v, 1 - (dx * dx) / (q.rx * q.rx) - ((y - q.y) ** 2) / (q.ry * q.ry));
    }
    if (v * 1.4 < bayer(x, y) + 0.12) continue;
    p.put(x, y, y > def.h * 0.55 ? (v > 0.5 ? '#4a4366' : '#363657') : (v > 0.55 ? '#2c3456' : '#222b4b'));
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= The city behind: a hazy far skyline, Bukit Nanas and Menara KL, the towers round the twins ================= */
const WARM = ['#f4c27a', '#e8ad62', '#ffd896'], COOL = ['#b6c8ec', '#94aad6', '#d6e2fa'];
const WINS = [], WINS_FAR = [];     // windows that switch on and off (the towers round the twins, the far city): { id, x, y, col, n, off }
const AVI = [];                     // red aviation lamps: { x, y, per, ph }
// Menara KL from the antenna down: [y0, y1, half-width, part]. Real: 421 m to the tip, the roof at 336.5 m (the antenna
// 0.20 of the height), the pod round the 276 m observation deck (pod and cap about 0.18), the shaft the rest; the pod
// is about three shafts wide: a flared bowl, the glazed observation ring, the sky deck's drum, a red turret.
const MKL_ROWS = [
  [36, 49, 0, 'ant'], [50, 57, 1, 'mast'], [58, 60, 2, 'turret'], [61, 62, 4, 'cap'], [63, 65, 5, 'deck'], [66, 66, 7, 'rim'],
  [67, 68, 7, 'obs'], [69, 70, 6, 'obs'], [71, 71, 5, 'bowl'], [72, 72, 4, 'bowl'], [73, 74, 3, 'bowl'], [75, 77, 2, 'neck'],
  [78, 146, 2, 'shaft'],
];
function renderFar() {
  const p = painter(W, H), r = rng(31);
  // far haze: mid-rise blocks with lit tops and a value step between neighbours; a few of their windows switch
  let x = -4;
  while (x < W) {
    const w = 7 + Math.floor(r() * 16), top = 106 + Math.floor(r() * 44), body = r() < 0.5 ? '#121a33' : '#151e3a';
    for (let y = top; y < 168; y++) for (let i = x; i < x + w; i++) p.put(i, y, i === x ? '#232d50' : body);
    for (let i = x; i < x + w; i++) p.put(i, top, '#2d3860');
    if (r() < 0.35) { const mx = x + 2 + Math.floor(r() * (w - 4)); p.rect(mx, top - 3, 3, 3, body); p.put(mx + 1, top - 3, '#2d3860'); }
    if (r() < 0.25 && top < 124) AVI.push({ x: x + (w >> 1), y: top - 1, per: 2 + Math.floor(r() * 2), ph: r() });
    for (let wy = top + 3; wy < 166; wy += 3) for (let wx = x + 2; wx < x + w - 1; wx += 2) {
      const v = r();
      if (v < 0.06) p.put(wx, wy, r() < 0.62 ? '#8c7356' : '#4c5884');
      else if (v < 0.13) WINS_FAR.push({ id: 900 + WINS_FAR.length, x: wx, y: wy, col: r() < 0.6 ? '#a88660' : '#6a78a8', n: 8 + Math.floor(r() * 12), off: r() });
    }
    x += w + (r() < 0.3 ? 1 : 0);
  }
  return p.done();
}
// Menara KL on its hill: its own look is a cool white floodlit shaft, the pod's glazed ring in blue with a rim of
// lamps and a red turret; `tint` washes the shaft and the pod in another colour (the timed colour wash)
function renderMKL(tint) {
  const p = painter(W, MKL.base + 1);
  for (const [y0, y1, hw, part] of MKL_ROWS) for (let y = y0; y <= y1; y++) for (let i = -hw; i <= hw; i++) {
    const X = MKL.x + i, f = hw ? i / hw : 0;
    let col;
    if (part === 'ant') col = i === 0 ? (y < 41 ? '#e6eaf4' : y % 4 === 0 ? '#c8443c' : '#aab2c6') : null;
    else if (part === 'mast') col = i === -1 ? '#c8d0e2' : i === 1 ? '#5a6280' : (y & 1) ? '#8a92aa' : '#d03c34';
    else if (part === 'turret') col = f < -0.3 ? '#ff6a5a' : f > 0.4 ? '#8c2228' : '#d8403a';
    else if (part === 'cap') col = f < -0.5 ? '#e4eaf6' : f > 0.5 ? '#6a7490' : '#b4bed4';
    else if (part === 'deck') col = y === 64 && (i & 1) ? '#bfe6ff' : f < -0.6 ? '#7ab4ec' : f > 0.6 ? '#2a4e8a' : '#4c86d0';
    else if (part === 'rim') col = (i & 1) ? '#f2f8ff' : '#9ccaf4';
    else if (part === 'obs') col = (i & 1) && y === 68 ? '#d8f0ff' : f < -0.55 ? '#6aa8ea' : f > 0.55 ? '#1e3c76' : (y === 70 ? '#2c5aa0' : '#3c76c4');
    else if (part === 'bowl') col = f < -0.4 ? '#7a8cb4' : f > 0.4 ? '#262f4c' : '#46557e';
    else if (part === 'neck') col = i < 0 ? '#9eaacb' : i > 0 ? '#3a4466' : '#6a7698';
    else col = i === -2 ? '#e6ecf8' : i === -1 ? '#c6d0e6' : i === 0 ? '#a2aecb' : i === 1 ? '#76829f' : '#4c5676';
    if (col && tint && (part === 'shaft' || part === 'neck' || part === 'deck' || part === 'obs' || part === 'bowl' || part === 'cap'))
      col = mixHex(col, tint, part === 'shaft' ? 0.62 : 0.72);
    if (col) p.put(X, y, col);
  }
  return p.done();
}
// Bukit Nanas: the forest reserve the tower stands in, a dome of rounded crowns, moonlit on top, lamplit at the tower's foot
function renderHill() {
  const p = painter(W, H), crest = i => 141 + ((i - MKL.x) / 56) ** 2 * 18;
  for (let i = 96; i < 222; i++) {
    const top = Math.round(crest(i) - 2.4 * Math.abs(Math.sin(i * 0.53)) - 1.6 * Math.abs(Math.sin(i * 0.21 + 1)));
    for (let y = top; y < 170; y++) {
      const d = y - top, lamp = Math.max(0, 1 - Math.hypot((i - MKL.x) / 18, (y - 150) / 9));
      let col = d === 0 ? '#1a3330' : d === 1 ? '#13282a' : '#0b1719';
      if (d > 1 && ((i * 7 + y * 3) % 11 === 0)) col = '#102222';
      if (lamp > 0.15 + bayer(i, y) * 0.6) col = lamp > 0.6 ? '#4c5a3e' : '#2a3a2c';
      p.put(i, y, col);
    }
  }
  return p.done();
}
// the colour wash's sequence: own look, green, blue, violet, pink, own look; [canvas a, canvas b, mix]
const WASH_TINTS = ['#3aff8a', '#3a8aff', '#a05aff', '#ff5ab4'];
function mklMix(t) {
  if (t < WASH[0] || t >= WASH[1]) return [0, 0, 0];
  const n = WASH_TINTS.length + 1, u = (t - WASH[0]) / (WASH[1] - WASH[0]) * n, k = Math.floor(u);
  const a = k === 0 ? 0 : k, b = k + 1 >= n ? 0 : k + 1;
  return [a, b, smooth(0.6, 1, u - k)];
}
// the towers between the hill and the twins: [x0, width, top, style]
const MIDS = [[110, 16, 128, 'block'], [186, 31, 110, 'hotel'], [296, 26, 90, 'round'], [322, 28, 114, 'block'],
  [351, 21, 46, 'glass'], [372, 27, 92, 'block'], [399, 30, 104, 'block'], [428, 26, 120, 'block']];
function renderMids() {
  const p = painter(W, H), r = rng(57);
  const win = (wx, wy, chance, warm) => {
    if (r() >= chance) return;
    const col = r() < warm ? WARM[Math.floor(r() * 3)] : COOL[Math.floor(r() * 3)];
    if (r() < 0.22) WINS.push({ id: WINS.length, x: wx, y: wy, col, n: 8 + Math.floor(r() * 12), off: r() });
    else p.put(wx, wy, col);
  };
  for (const [x0, w, top, style] of MIDS) {
    const x1 = x0 + w - 1, mid = x0 + (w >> 1);
    if (style === 'block') {
      const body = r() < 0.5 ? '#1a2240' : '#1d2646';
      for (let y = top; y < 170; y++) for (let i = x0; i <= x1; i++) p.put(i, y, i === x0 ? '#38446c' : i === x1 ? '#11172c' : body);
      for (let i = x0; i <= x1; i++) { p.put(i, top, '#4a5680'); p.put(i, top + 1, '#2a3456'); }
      p.rect(x0 + 3, top - 4, Math.min(8, w - 6), 4, body); for (let i = x0 + 3; i < x0 + 3 + Math.min(8, w - 6); i++) p.put(i, top - 4, '#3c4870');
      AVI.push({ x: x0 + 4, y: top - 5, per: 3, ph: r() });
      for (let wy = top + 4; wy < 166; wy += 3) {
        if (r() < 0.04) { for (let wx = x0 + 2; wx < x1; wx++) p.put(wx, wy, (wx - x0) % 4 === 1 ? '#5a6a90' : COOL[1]); continue; }
        for (let wx = x0 + 2; wx < x1; wx += 2) win(wx, wy, 0.17, 0.55);
      }
    } else if (style === 'hotel') {
      // a hotel slab with a shallow hipped crown and a ring of warm crown lamps, rooms lit warm in rows
      for (let y = top; y < 170; y++) for (let i = x0; i <= x1; i++) p.put(i, y, i < x0 + 2 ? '#3a4266' : i > x1 - 2 ? '#141a30' : '#1e2440');
      for (let k = 0; k < 7; k++) for (let i = x0 + k; i <= x1 - k; i++) p.put(i, top - 1 - k, k === 6 ? '#e8c890' : i < mid ? '#5a5a7a' : '#34385a');
      for (let i = x0; i <= x1; i++) p.put(i, top, (i & 1) ? '#ffe2a6' : '#c89a5e');
      for (let wy = top + 4; wy < 166; wy += 2) for (let wx = x0 + 2; wx < x1 - 1; wx += 2) win(wx, wy, 0.36, 0.85);
    } else if (style === 'round') {
      // a round office tower: banded floors on a lit cylinder, a ring of blue lamps round its crown
      for (let y = top; y < 170; y++) for (let i = x0; i <= x1; i++) {
        const f = (i - mid) / (w / 2), lv = 0.85 - f * 0.35 - f * f * 0.35, band = (y - top) % 3 === 0;
        p.put(i, y, pick(['#121a32', '#1a2646', '#24345c', '#304470', '#40568a'], band ? lv * 0.7 : lv, i, y));
      }
      for (let y = top - 6; y < top; y++) { const hw = Math.round((w / 2 - 2) * (1 - (top - y) / 9)); for (let i = mid - hw; i <= mid + hw; i++) p.put(i, y, i < mid ? '#33446e' : '#202c4c'); }
      for (let i = x0 + 1; i < x1; i++) { p.put(i, top + 1, (i & 1) ? '#62c4ff' : '#2a8ae6'); p.put(i, top + 2, '#1a3c78'); }
      for (let y = top - 14; y < top - 6; y++) p.put(mid, y, '#6a7494');
      AVI.push({ x: mid, y: top - 15, per: 2, ph: r() });
      for (let wy = top + 5; wy < 166; wy += 3) for (let wx = x0 + 3; wx < x1 - 1; wx += 2) win(wx, wy, 0.22, 0.5);
    } else {
      // a slender glass tower with a sloped crown: vertical fins, white edge strips, a lit cut across the top
      for (let i = x0; i <= x1; i++) {
        const yt = top + Math.round((i - x0) * 0.62);
        for (let y = yt; y < 170; y++) {
          const fin = (i - x0) % 3 === 0;
          p.put(i, y, i === x0 ? '#7e90ba' : i === x1 ? '#2a3658' : fin ? '#2a3a62' : '#18233f');
        }
        p.put(i, yt, '#b4c4e2'); p.put(i, yt + 1, '#6c7ea8');
      }
      AVI.push({ x: x0, y: top - 1, per: 2, ph: r() });
      for (let wy = top + 16; wy < 166; wy += 3) for (let wx = x0 + 2; wx < x1; wx += 3) win(wx, wy, 0.16, 0.3);
    }
  }
  return p.done();
}

/* ================= The Petronas Twin Towers ================= */
// The plan is a Rub el Hizb, an eight-pointed star with a round lobe in each of its eight inner corners, so seen from
// the side the shaft is fluted: a sharp lit rib at each point, a rounded lobe between, dark valleys in between. The
// floors' sunshades glow as fine horizontal lines. Above the shaft the crown steps back nine times, the tiers shorter
// and the steps quicker towards the top (an ogive, not an even taper), each ledge a ring of light with the star's points
// standing up from it as small chevrons; then the lit lantern, the ball, and the spire with its two rings.
const TIER_ROWS = [];               // [y0, y1, half-width] from the shaft up
{ let y = SHAFT_TOP - 1; for (const [rows, hw] of TIERS) { TIER_ROWS.push([y - rows + 1, y, hw]); y -= rows; } }
function towerHW(y) {
  if (y >= SHAFT_TOP && y <= TOW.base) return TOW.hw;
  for (const [a, b, hw] of TIER_ROWS) if (y >= a && y <= b) return hw;
  if (y >= BALL[0] && y <= BALL[1]) return 1;
  if (y >= NEEDLE[0] && y < BALL[0]) return 0;
  return -1;
}
const tierTop = y => TIER_ROWS.find(([a]) => a === y);
// the star's sixteen faces seen from the side: points (P) and lobes (A) at hw * sin(k * 22.5 deg)
const FEAT = [-1, -0.924, -0.707, -0.383, 0, 0.383, 0.707, 0.924, 1];
function ribKind(dx, hw) {
  if (hw <= 3) return dx === 0 ? 'P' : Math.abs(dx) === hw ? 'E' : 'V';
  let best = 9, kind = 'V';
  FEAT.forEach((f, k) => {
    const c = Math.round(f * hw), d = Math.abs(dx - c);
    if (d < best) { best = d; kind = (k & 1) ? (d === 0 ? 'A' : d === 1 ? 'a' : 'V') : (d === 0 ? 'P' : 'V'); }
  });
  return Math.abs(dx) === hw ? 'E' : kind;
}
const STEEL = ['#161b2c', '#212839', '#2e364b', '#3f485f', '#535d76', '#6a758f', '#8590aa', '#a3adc4', '#c2cadb', '#dde2ec', '#f2f4f8'];
const SPARKS = [];                  // warm lit windows that twinkle: { x, y, k, ph }
const TOWER_WARM = ['#ffd68a', '#f2bc6a', '#ffe6b4'];
function towerPixel(dx, y, hw, cx, r) {
  const f = dx / Math.max(1, hw), kind = ribKind(dx, hw), crown = y < SHAFT_TOP;
  let L = 0.36 + 0.14 * (1 - f * f) - 0.12 * f;                   // round, washed from the left
  L += kind === 'P' ? 0.4 : kind === 'A' ? 0.2 : kind === 'a' ? 0.05 : kind === 'E' ? (dx < 0 ? 0.26 : -0.1) : -0.17;
  L += (y & 1) ? 0.04 : -0.03;                                      // the sunshades' lit lines
  if (crown) L += 0.04 + 0.1 * (SHAFT_TOP - y) / (SHAFT_TOP - CROWN_TOP);   // the floodlit crown glows whiter towards the top
  if (y > 128) L -= (y - 128) / 46 * 0.16;                          // the shaft darkens towards the mall
  if (!(y & 1) && (kind === 'V' || kind === 'a')) {                 // lit office windows, warm, in bands round the skybridge
    const band = y >= 116 && y <= 128 ? 0.2 : y >= 86 && y <= 92 ? 0.1 : 0.02;
    if (r() < band) {
      const col = TOWER_WARM[Math.floor(r() * 3)];
      if (r() < 0.3) SPARKS.push({ x: cx + dx, y, k: 7 + Math.floor(r() * 30), ph: r(), col });
      return col;
    }
  }
  return pick(STEEL, L, cx + dx, y);
}
function renderTowers() {
  const p = painter(W, H);
  for (const cx of [TOW.l, TOW.r]) {
    const r = rng(cx);
    for (let y = NEEDLE[0]; y <= TOW.base; y++) {
      const hw = towerHW(y);
      if (y < BALL[0]) {                                            // the spire, its tip and its two rings
        p.put(cx, y, y === NEEDLE[0] ? '#ffffff' : y < 15 ? '#e2e8f2' : '#c4ccdc');
        if (y >= 15) p.put(cx + 1, y, '#6e7892');                                     // the spire thickens towards its foot
        if (y === 18 || y === 20) { p.put(cx - 1, y, '#e8ecf4'); p.put(cx + 1, y, '#a8b0c4'); p.put(cx + 2, y, '#5a6480'); }   // its rings
        continue;
      }
      if (y <= BALL[1]) {                                           // the ball on a short dark neck
        if (y === BALL[1]) { p.put(cx, y, '#5a6070'); p.put(cx + 1, y, '#3a4050'); continue; }
        const row = y - BALL[0];
        p.put(cx - 1, y, row === 1 ? '#fff4dc' : '#e8dcc0'); p.put(cx, y, '#ffffff'); p.put(cx + 1, y, row === 0 ? '#f6eedc' : '#d0c4a8');
        if (row === 1) p.put(cx + 2, y, '#a89c84');
        continue;
      }
      if (y < CROWN_TOP + 3) {                                      // the lit lantern under the ball
        for (let dx = -hw; dx <= hw; dx++) p.put(cx + dx, y, dx === -hw ? '#f6e6c4' : dx === hw ? '#9a9080' : (y === CROWN_TOP ? '#fff8e6' : (dx & 1) ? '#c8c0ae' : '#f0e4c8'));
        continue;
      }
      const top = tierTop(y);
      for (let dx = -hw; dx <= hw; dx++) {
        let col = towerPixel(dx, y, hw, cx, r);
        if (top) { const k = ribKind(dx, hw); col = k === 'P' ? '#fff6e4' : k === 'A' ? '#e6e0d2' : dx === hw ? '#8a867c' : k === 'E' ? '#e8e2d4' : dx > 0 ? '#a8a49a' : '#c6c0b2'; }   // the ledge: lit at the star's points
        else if (tierTop(y - 1) && ribKind(dx, hw) === 'P') col = '#fff3d8';                        // the star's points rise from it
        else if (tierTop(y - 1) && Math.abs(dx) === hw) col = dx < 0 ? '#e4e0d6' : '#8a8a96';
        p.put(cx + dx, y, col);
      }
      // the star's points stand up over each ledge as chevrons: a lit tip one row above the ring where the tier above is narrower
      if (top) FEAT.forEach((f, k) => {                              // each point of the star rises from the ledge as a small chevron
        if (k & 1) return;
        const c = Math.round(f * hw);
        if (Math.abs(c) < hw && y - 1 >= CROWN_TOP + 3) p.put(cx + c, y - 1, '#f6eedc');
      });
    }
  }
  // the skybridge: two decks of lit glass between steel beams, a pivot at its middle and the inverted-V legs down to level 29
  const { x0, x1, y0 } = BRIDGE, mid = (TOW.l + TOW.r) >> 1;
  for (let i = x0 - 1; i <= x1 + 1; i++) {
    const m = (i - x0) % 3 === 0;
    p.put(i, y0, '#e2e6ee');
    p.put(i, y0 + 1, m ? '#6a7490' : (i < mid ? '#ffe2a2' : '#fbd794'));
    p.put(i, y0 + 2, '#a8b0c4');
    p.put(i, y0 + 3, m ? '#5c6682' : '#e2ecfa');
    p.put(i, y0 + 4, '#6c7690');
  }
  p.put(mid - 1, y0 + 5, '#9aa4ba'); p.put(mid, y0 + 5, '#e6eaf2'); p.put(mid + 1, y0 + 5, '#9aa4ba');
  lineP(p.put, mid, y0 + 6, x0, BRIDGE.leg, '#d0d6e2'); lineP(p.put, mid + 1, y0 + 6, x0 + 1, BRIDGE.leg, '#59627c');
  lineP(p.put, mid, y0 + 6, x1, BRIDGE.leg, '#c4cada'); lineP(p.put, mid - 1, y0 + 6, x1 - 1, BRIDGE.leg, '#59627c');
  return p.done();
}

/* ================= Suria KLCC's lake front and the esplanade ================= */
const MALL_BAYS = [];                                    // the lit shopfronts, some of which switch off and on
function renderMall() {
  const p = painter(W, H), r = rng(77), { x0, x1, roof, atrium } = MALL, mid = (TOW.l + TOW.r) >> 1;
  // the glass atrium at the middle, under the gap between the towers, its curved roof rimmed with light
  for (let i = mid - 18; i <= mid + 18; i++) {
    const yt = atrium + Math.round(((i - mid) / 18) ** 2 * 5);
    for (let y = yt; y < roof; y++) p.put(i, y, y === yt ? '#c8d2ea' : (i - mid) % 4 === 0 ? '#3a4466' : (y & 1) ? '#5e6e98' : '#4c5a84');
  }
  // bays of varied width: shops lit warm or cool, a few dark, restaurants under awnings of different colours
  const bays = [];
  for (let bx = x0; bx <= x1;) { const w = 5 + Math.floor(r() * 7); bays.push({ a: bx, b: Math.min(x1, bx + w - 1), up: r(), shop: r(), aw: Math.floor(r() * 6), lit: r() }); bx += w; }
  MALL_BAYS.push(...bays.filter(B => B.shop >= 0.18 && B.b - B.a > 3));
  const SHOP = [['#ffd394', '#e2a660'], ['#ffe6b8', '#d8a870'], ['#c4d6f4', '#8ea6d4'], ['#a8f0e0', '#5ab0a0'], ['#f4c4e0', '#b07a9a']];
  const AWN = ['#8c4a3c', '#c2a882', '#3c6a4c', '#7a3c4a', '#2e4a6a', '#a8743a'];
  for (const B of bays) for (let i = B.a; i <= B.b; i++) {
    const edge = i === B.a, end = i < x0 + 2 || i > x1 - 2;
    for (let y = roof; y < SHORE; y++) {
      let col;
      if (y === roof) col = '#5c5874';
      else if (y < 157) col = y === 154 && !edge && B.up < 0.65 ? (B.up < 0.4 ? '#9e7650' : '#6a6890') : '#26243a';
      else if (y === 157 || y === 164) col = '#4e4964';
      else if (y < 164) {                                            // level 2: glass shopfronts between piers
        if (edge) col = '#2a2840';
        else if (B.shop < 0.18) col = '#2c2a42';
        else { const sh = SHOP[Math.floor(B.shop * 7) % 5]; col = y === 158 ? sh[0] : (y === 163 ? mixHex(sh[1], '#000000', 0.25) : sh[1]); if ((i - B.a) % 4 === 0) col = mixHex(col, '#1a1828', 0.5); }
      } else if (y < 167) {                                          // level 1: restaurant awnings over the terrace
        const aw = AWN[B.aw];
        col = B.lit < 0.15 ? '#2a2436' : y === 165 ? mixHex(aw, '#ffe0b0', 0.25) : aw;
        if (y === 166 && (i - B.a) % 2 === 1 && B.lit >= 0.15) col = mixHex(aw, '#000000', 0.3);
      } else if (y < 171) col = edge ? '#3a3046' : B.lit < 0.3 ? '#3a2e3e' : (y === 167 ? '#ffd28c' : B.lit < 0.6 ? '#d89a5c' : '#c88450');
      else if (y < 173) col = '#342e44';
      else col = y === 173 ? '#5a5268' : '#7a7088';                   // the esplanade's granite lip
      if (end) col = mixHex(col, '#141428', 0.5);
      if (y > roof && y < 173) col = mixHex(col, '#1a1626', y < 164 ? 0.22 : 0.34);   // seen across the lake, softened by the damp air
      p.put(i, y, col);
    }
  }
  // lamp posts along the esplanade, small and warm
  for (let i = x0 + 9; i < x1; i += 23) { p.put(i, 168, '#2a2434'); p.put(i, 169, '#2a2434'); p.put(i, 170, '#2a2434'); p.put(i, 167, '#ffe6b0'); }
  return p.done();
}

// the city's small life: shopfronts switching off and on, diners moving on the restaurant terraces, the glass tower's
// LED edge running up every 8 s, the round tower's crown ring drifting from blue through teal to violet
const TERRACE = Array.from({ length: 22 }, (_, i) => ({ x: MALL.x0 + 8 + Math.floor(hash(i, 1, 55) * (MALL.x1 - MALL.x0 - 16)), k: 3 + Math.floor(hash(i, 2, 55) * 6), ph: hash(i, 3, 55), r: frac((i + 0.5) * 0.618) }));
const RING = ['#62c4ff', '#4ae0d0', '#8a7aff', '#62c4ff'];
function drawCityLife(t, crowd) {
  MALL_BAYS.forEach((B, i) => {
    if (hash(i, 9, 55) > 0.8) return;
    const n = 10 + Math.floor(hash(i, 8, 55) * 10);
    if (hash(i, Math.floor(frac(t / LOOP + hash(i, 7, 55)) * n), 56) < 0.6) return;
    ctx.fillStyle = '#2a2638'; ctx.fillRect(B.a + 1, 158, B.b - B.a, 6);
  });
  ctx.fillStyle = '#241a24';
  for (const f of TERRACE) { if (f.r >= crowd) continue; const x = f.x + Math.round(wave(t, f.k, f.ph) * 3); ctx.fillRect(x, 167, 1, 3); ctx.fillRect(x + 1, 168, 1, 2); }
  const [gx0, , gtop] = MIDS[4], u = frac(t / 8), gy = Math.round(166 - u * (166 - gtop));
  ctx.fillStyle = '#d8ecff'; ctx.fillRect(gx0, Math.max(gtop, gy - 6), 1, Math.min(6, 166 - gy + 6));
  ctx.fillStyle = '#9ab8e8'; ctx.fillRect(gx0, Math.max(gtop, gy + 1), 1, 3);
  const [rx0, rw, rtop] = MIDS[2], q = frac(t / 60) * 3, k = Math.floor(q), c = mixHex(RING[k], RING[k + 1], Math.round(smooth(0.5, 1, q - k) * 6) / 6);
  for (let i = rx0 + 1; i < rx0 + rw - 1; i++) if (i & 1) px(ctx, i, rtop + 1, c);
}

/* ================= Lake Symphony: still water, the mirror, the fountain line ================= */
function renderWater() {
  const p = painter(W, KERB - SHORE);
  for (let y = 0; y < KERB - SHORE; y++) for (let i = 0; i < W; i++) {
    const v = 0.15 + 0.5 * y / (KERB - SHORE) + 0.12 * Math.sin(i * 0.11 + y * 1.7) * Math.sin(i * 0.031 - y);
    p.put(i, y, pick(['#040915', '#071022', '#0a162e', '#0e1c38'], v, i, y));
  }
  return p.done();
}
// The fountain line along the lake, as Lake Symphony's shows play it. Between shows only the bubblers foam low under a
// little mist. A show builds up: the foam rises, then the candle jets climb from the middle outwards. They dance in
// three figures (a swell rolling along the line, odd and even jets taking turns, a pulse from the middle out), each
// jet with its own height, sway and phase and a soft plume of spray on top that falls back in drops. Between them,
// arcs thrown towards each other cross, each at its own height. A tall finale, then all falls back to foam.
const lineY = x => 184 + Math.round(((x - 254) / 112) ** 2 * 3);
const CANDLES = [];
{ const r = rng(91);
  [143, 171, 202, 229, 254, 279, 305, 334, 364].forEach((x, i) => CANDLES.push({ x, i, base: lineY(x),
    top: [0.5, 0.64, 0.74, 0.86, 1, 0.86, 0.76, 0.62, 0.5][i] * (0.88 + r() * 0.2), ph: r() * TAU, sw: 0.9 + r() * 1.4, swPh: r() * TAU,
    w: i === 4 ? 3 : 2, odd: i & 1 }));
}
const ARCS = [];
{ const r = rng(92);
  for (let c = 0; c < CANDLES.length - 1; c++) for (const k0 of [0, 1]) ARCS.push({ c, k0, apex: 0.7 + r() * 0.5, len: 0.78 + r() * 0.3, ph: r() * TAU });
}
const BUBBLERS = [];                                     // foaming nozzles: in clumps round each candle, a few between
{ const r = rng(93);
  for (const c of CANDLES) for (let k = 0; k < 4; k++) { const x = c.x + Math.round((r() - 0.5) * 12); BUBBLERS.push({ id: BUBBLERS.length, x, base: lineY(x) + 3 + Math.floor(r() * 3) }); }
  for (let k = 0; k < 12; k++) { const x = 140 + Math.round(r() * 230); BUBBLERS.push({ id: BUBBLERS.length, x, base: lineY(x) + 3 + Math.floor(r() * 3) }); }
}
const JET_TINTS = { white: ['#fbfdff', '#dce6f4', '#a8bcd8'], warm: ['#fff6e4', '#ffe2b0', '#e2b878'], cyan: ['#f2fbff', '#9ee0ff', '#4aa8e8'],
  rose: ['#fff2fa', '#ffb4dc', '#d85aa8'], violet: ['#f6f0ff', '#c8aeff', '#7a5ae0'] };
// the show's state at t: bub (the foam, 0.25 when calm), rise(i) (each candle's climb), amp, arcs, the dance's three
// figures as weights, the finale, the light (two tints and a quantised mix between them)
const CALM = { bub: 0.25, show: null, arcs: 0, fin: 0, tA: 'white', tB: 'white', mix: 0 };
function showState(t) {
  const S = SHOWS.find(s => t >= s.a && t < s.b);
  if (!S) return CALM;
  const bub = 0.25 + 0.75 * smooth(S.a, S.rise, t) * (1 - smooth(S.fall + 2, S.b, t));
  const arcs = smooth(S.dance - 2, S.dance + 3, t) * (1 - smooth(S.fall - 2, S.fall + 3, t));
  const fin = smooth(S.fin, S.fin + 2.5, t) * (1 - smooth(S.fall - 1, S.fall + 3, t));
  const seg = (S.fin - S.dance) / S.tints.length, k = clamp((t - S.dance) / seg, 0, S.tints.length - 1);
  const ka = Math.floor(k), mix = Math.round(smooth(0.7, 1, k - ka) * 8) / 8;
  const d = clamp((t - S.dance) / (S.fin - S.dance), 0, 1);
  const wave3 = [1 - smooth(0.28, 0.4, d), smooth(0.28, 0.4, d) * (1 - smooth(0.62, 0.74, d)), smooth(0.62, 0.74, d)];
  return { bub, show: S, arcs, fin, tA: S.tints[ka], tB: S.tints[Math.min(S.tints.length - 1, ka + 1)], mix, wave3, t };
}
function candleHeight(c, St) {
  const S = St.show;
  if (!S) return 0;
  const delay = Math.abs(c.i - 4) * 1.4, rise = smooth(S.rise + delay, S.rise + delay + 5, St.t) * (1 - smooth(S.fall + delay * 0.3, S.fall + 4 + delay * 0.3, St.t));
  if (rise <= 0) return 0;
  const T = St.t, [wA, wB, wC] = St.wave3;
  const swell = 0.5 + 0.5 * Math.sin(TAU * 21 * T / LOOP - c.i * 0.8 + c.ph * 0.3);
  const turns = 0.5 + 0.5 * Math.sin(TAU * 30 * T / LOOP + (c.odd ? Math.PI : 0) + c.ph * 0.2);
  const pulse = 0.5 + 0.5 * Math.sin(TAU * 40 * T / LOOP - Math.abs(c.i - 4) * 1.1);
  const dance = wA * (0.45 + 0.55 * swell) + wB * (0.35 + 0.65 * turns) + wC * (0.4 + 0.6 * pulse);
  const lvl = lerp(0.55, dance, smooth(S.dance - 3, S.dance + 2, T));
  return 44 * c.top * rise * lerp(lvl, c.i === 4 ? 1.3 : 1.1, St.fin);
}
const tintCol = (St, k) => St.mix > 0 ? mixHex(JET_TINTS[St.tA][k], JET_TINTS[St.tB][k], St.mix) : JET_TINTS[St.tA][k];
function sprayDot(x, y, a, col) { ctx.globalAlpha = a; ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); }
function drawFountain(t) {
  const St = showState(t), T = [tintCol(St, 0), tintCol(St, 1), tintCol(St, 2)], st = step(t, 1440), st2 = step(t, 480);
  // the light the show throws on the water
  if (St.show) halo(HL['jet_' + St.tA], 254, 186, 0.8 * (St.bub - 0.25) / 0.75);
  // mist over the nozzles
  for (let x = 136; x < 372; x += 1) for (let k = 0; k < 7; k++)
    if (hash(x, k, step(t, 1200)) < 0.2 * (1 - k / 8) * St.bub) sprayDot(x, lineY(x) + 3 - k - Math.round(St.bub * 3 * (k / 7)), 0.3, T[2]);
  // the bubblers: low foaming domes, irregular
  for (const b of BUBBLERS) {
    const hgt = Math.round((0.5 + hash(b.id, 7, st2) * 2.5) * (0.5 + St.bub * 1.8));
    for (let k = 0; k < hgt; k++) if (hash(b.id, k, st) < 0.7 - k * 0.1) sprayDot(b.x + (k > 0 && hash(b.id, k + 4, st) < 0.5 ? (hash(b.id, k + 5, st) < 0.5 ? -1 : 1) : 0), b.base - k, 0.5 - k * 0.05, T[k ? 1 : 0]);
  }
  if (!St.show) { ctx.globalAlpha = 1; return; }
  // the arcs, each its own height and reach, crossing between the candles
  if (St.arcs > 0.02) for (const a of ARCS) {
    const A = CANDLES[a.c], B = CANDLES[a.c + 1], from = a.k0 ? B : A, dir = a.k0 ? -1 : 1, reach = B.x - A.x;
    const apex = St.arcs * (6 + 12 * Math.min(A.top, B.top) * a.apex * (0.7 + 0.3 * Math.sin(TAU * 15 * t / LOOP + a.ph)));
    if (apex < 2) continue;
    const n = Math.round(reach * 1.2);
    let lx = -99, ly = -99;
    for (let k = 0; k <= n; k++) {
      const u = k / n, x = Math.round(from.x + dir * reach * a.len * u), y = Math.round(from.base - 4 * apex * u * (1 - u));
      if (x === lx && y === ly) continue;
      lx = x; ly = y;
      if (u > 0.65 && hash(a.c * 9 + a.k0, k, st) < (u - 0.65) * 2.4) continue;   // the stream breaks up as it falls
      sprayDot(x, y, (0.75 - 0.35 * u) * Math.min(1, St.arcs * 1.5), u < 0.5 ? T[0] : T[1]);
    }
  }
  // the candles
  for (const c of CANDLES) {
    const hh = Math.round(candleHeight(c, St));
    if (hh < 3) continue;
    const sway = c.sw * Math.sin(TAU * 30 * t / LOOP + c.swPh);
    for (let k = 1; k < 10; k++) if (hash(c.i, k, step(t, 960)) < 0.5) sprayDot(c.x + (k & 1), c.base + k, 0.3 * (1 - k / 10), T[1]);   // its reflection
    for (let k = 0; k < hh; k++) {
      const u = k / hh, x = Math.round(c.x + sway * u * u), y = c.base - k;
      ctx.globalAlpha = 0.92 - 0.4 * u; ctx.fillStyle = u < 0.45 ? T[0] : (k & 1) ? T[0] : T[1];
      ctx.fillRect(x - (c.w > 2 && u < 0.4 ? 1 : 0), y, u < 0.4 ? c.w : u < 0.7 ? 2 : 1, 1);
      if (u > 0.3 && hash(c.i, k, st) < u * 0.75) sprayDot(x + (hash(c.i, k, st + 1) < 0.5 ? -1 : 2), y, 0.42, T[1]);   // spray shed on the way up
    }
    // a soft plume at the top, bigger on the taller jets
    const px0 = c.x + sway, py0 = c.base - hh, pr = 2.5 + hh / 14;
    for (let y = -Math.ceil(pr); y <= Math.ceil(pr); y++) for (let i = -Math.ceil(pr + 1); i <= Math.ceil(pr + 1); i++) {
      const d = (i * i) / ((pr + 1) * (pr + 1)) + (y * y) / (pr * pr);
      if (d < 1 && hash(c.i * 64 + i + 9, y + 9, st) < 0.8 - d * 0.55) sprayDot(px0 + i, py0 + y + 1, 0.62 - d * 0.35, d < 0.3 ? T[0] : T[1]);
    }
    for (let d = 0; d < 12; d++) {
      const s = frac(t * (216 + Math.floor(hash(c.i, d) * 120)) / LOOP + hash(c.i, d, 2)), dir = hash(c.i, d, 3) < 0.5 ? -1 : 1;
      const x = px0 + dir * (2 + hash(c.i, d, 4) * (pr + 3)) * Math.sqrt(s), y = py0 + 2 + hh * 0.95 * s * s;
      if (y < c.base) sprayDot(x, y, 0.65 * (1 - s * 0.6), T[s < 0.3 ? 0 : 1]);
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= The lakeside path, the jogging track, the planting along the front ================= */
const BOLLARDS = [196, 312];
const PUDDLES = [{ x0: 190, x1: 318, y0: 213, y1: 231, mirror: true }, { x0: 322, x1: 374, y0: 209, y1: 215 }];
const inPuddle = (i, y) => PUDDLES.find(q => {
  const cx = (q.x0 + q.x1) / 2, cy = (q.y0 + q.y1) / 2, rx = (q.x1 - q.x0) / 2, ry = (q.y1 - q.y0) / 2;
  return ((i - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 + 0.18 * Math.sin(i * 0.4 + y) < 1;
});
// the track bows towards us at its ends and runs off the bottom on the left, in front of the mamak's tables
const trackTop = x => TRACK + Math.round(((x - 330) / 150) ** 2 * 5) + (x < 190 ? Math.round(((190 - x) / 18) ** 2) : 0);
const PAVE = ['#0b0a12', '#100f18', '#15141e', '#1b1925', '#22202d', '#2b2836'];
const RUBBER = ['#2a1416', '#38191a', '#46201e', '#562824', '#66302a'];
function renderPath() {
  const p = painter(W, H), VP = { x: 254, y: 150 };
  const rows = [203, 206, 210, 215, 221, 228];
  for (let y = KERB; y < H; y++) for (let i = 0; i < W; i++) {
    if (y < KERB + 2) { p.put(i, y, y === KERB ? ((i >> 2) & 1 ? '#6e6a7c' : '#625e70') : '#2c2a38'); continue; }
    const tt = trackTop(i);
    let col;
    if (y >= tt && y < tt + 13) {                                                   // the rubber jogging track, two lane lines
      const d = y - tt;
      let v = 0.45 + 0.2 * Math.sin(i * 0.07 + y) * Math.sin(i * 0.019) + 0.25 * Math.max(0, 1 - Math.abs(i - 254) / 90) * (1 - d / 13);
      col = d === 0 ? '#5a5462' : d === 12 ? '#141018' : (d === 4 || d === 9) && (i + y) % 7 !== 0 ? '#7a6a66' : pick(RUBBER, v, i, y);
    } else if (y >= tt + 13) {                                                      // the grass verge beyond it
      col = y === tt + 13 ? '#3a3a44' : pick(['#060c0a', '#0a1410', '#0e1a14', '#132218'], 0.35 + 0.3 * Math.sin(i * 0.5 + y * 0.8) * Math.sin(i * 0.13), i, y);
    } else {
      let v = 0.42 + 0.18 * Math.sin(i * 0.05 + y * 0.3) * Math.sin(i * 0.013 - y * 0.09);
      v += 0.22 * Math.max(0, 1 - Math.abs(i - 254) / 120) * Math.max(0, 1 - (y - KERB) / 50);   // the lake's light on the wet stone
      if (rows.includes(y)) v -= 0.28;
      const gx = VP.x + (i - VP.x) * (H - VP.y) / (y - VP.y);                        // joints running back towards the lake
      if (Math.abs(((gx % 46) + 46) % 46 - 23) < (y - VP.y) / (H - VP.y) * 0.9) v -= 0.22;
      col = pick(PAVE, v, i, y);
      if (inPuddle(i, y)) col = pick(['#070c1c', '#0b1428', '#101c36', '#16264a'], 0.5 + 0.3 * Math.sin(i * 0.3), i, y);
    }
    const pool = Math.max(0, 1 - Math.hypot((i - 60) / 100, (y - 210) / 36));       // the mamak's tubes on the wet ground
    if (pool > 0.08 && pool * 0.9 > bayer(i, y) + 0.1) col = mixHex(col, '#9ac8b4', pool > 0.55 ? 0.32 : pool > 0.3 ? 0.18 : 0.08);
    p.put(i, y, col);
  }
  // bollard lights at the track's edge
  for (const bx of BOLLARDS) {
    const by = trackTop(bx) - 1;
    for (let y = by - 8; y <= by; y++) { p.put(bx, y, '#4a4654'); p.put(bx + 1, y, '#2a2832'); p.put(bx + 2, y, '#1a1820'); }
    p.put(bx, by - 8, '#ffe6b0'); p.put(bx + 1, by - 8, '#ffd890'); p.put(bx + 2, by - 8, '#c8a060');
  }
  // park lamps: a slim post, a lantern box with a little hood
  for (const L of LAMPS) {
    for (let y = L.y + 3; y < 205; y++) { p.put(L.x, y, '#3a3646'); p.put(L.x + 1, y, '#1c1a26'); }
    p.rect(L.x - 2, 204, 5, 2, '#26222e');
    for (let i = -2; i <= 3; i++) p.put(L.x + i, L.y - 3, '#2a2634');
    p.rect(L.x - 1, L.y - 2, 4, 4, '#ffe2a4'); p.put(L.x - 1, L.y - 2, '#fff4d4'); p.put(L.x + 2, L.y + 1, '#e0b070');
  }
  // a granite planter at the bottom right
  for (let y = 252; y < H; y++) for (let i = 380; i < W; i++) p.put(i, y, y === 252 ? '#6a6674' : y < 256 ? '#3e3a46' : '#1c1a22');
  for (let i = 380; i < W; i += 14) for (let y = 253; y < H; y++) p.put(i, y, '#14121a');
  return p.done();
}
// The planting along the front: low ferns along the verge, and in the planter heliconia (red and gold bracts on tall
// stems), broad leaves and a frangipani with cream flowers. Each clump is a small sprite drawn swaying with the breeze.
const PLANTS = [];
function renderPlants() {
  const r = rng(71), out = [];
  const leafSprite = (w, h, kind, seed) => {
    const p = painter(w, h), rr = rng(seed), cx = w >> 1;
    if (kind === 'fern') {
      for (let f = 0; f < 7; f++) {
        const a = -Math.PI / 2 + (f - 3) * 0.36 + (rr() - 0.5) * 0.2, len = h * (0.7 + rr() * 0.3);
        for (let s = 0; s < len; s++) {
          const bend = (s / len) ** 2 * 3 * Math.sign(f - 3), x = cx + Math.cos(a) * s * 0.9 + bend, y = h - 1 + Math.sin(a) * s;
          p.put(x, y, s > len * 0.7 ? '#3e6a3a' : pick(LEAF, 0.45 + 0.35 * (s / len) + (s % 3 === 0 ? 0.15 : 0), Math.round(x), Math.round(y)));
          if (s > 2 && s % 2 === 0) { p.put(x - 1, y, LEAF[2]); p.put(x + 1, y + 1, LEAF[3]); }
        }
      }
    } else if (kind === 'heli') {
      for (let k = 0; k < 4; k++) {                                     // broad paddle leaves
        const lx = cx + (k - 1.5) * 4, lh = h * (0.6 + rr() * 0.35), lean = (k - 1.5) * 0.18;
        for (let s = 0; s < lh; s++) {
          const x = lx + lean * s, y = h - 1 - s, wdt = s > lh * 0.3 ? Math.round(2.4 * Math.sin(Math.PI * (s - lh * 0.3) / (lh * 0.7)) + 0.5) : 0;
          for (let j = -wdt; j <= wdt; j++) p.put(x + j, y, j === -wdt && wdt ? '#2e5a32' : pick(LEAF, 0.35 + 0.3 * (s / lh), Math.round(x + j), y));
          if (!wdt) p.put(x, y, '#1a3020');
        }
      }
      for (let k = 0; k < 3; k++) {                                     // the bracts: red with gold tips, zigzag on a stalk
        const bx = cx - 4 + k * 4 + Math.round(rr() * 2), by = Math.round(h * (0.25 + rr() * 0.3));
        for (let j = 0; j < 7; j++) { const x = bx + ((j & 1) ? 1 : -1), y = by + j; p.put(x, y, j % 2 ? '#c8402c' : '#e05a34'); p.put(x + ((j & 1) ? 1 : -1), y, '#e8b040'); }
        for (let y = by + 7; y < h; y++) p.put(bx, y, '#1e3a24');
      }
    } else {                                                            // frangipani: a forked grey stem, leaf rosettes, flowers
      for (let y = h - 1; y > h * 0.45; y--) p.put(cx, y, '#4a4440');
      for (const [ex, ey] of [[cx - 7, Math.round(h * 0.3)], [cx + 6, Math.round(h * 0.25)], [cx, Math.round(h * 0.15)]]) {
        lineP(p.put, cx, Math.round(h * 0.5), ex, ey + 3, '#5a524a');
        for (let k = 0; k < 7; k++) { const a = k / 7 * TAU, x = ex + Math.cos(a) * 4, y = ey + 2 + Math.sin(a) * 2; lineP(p.put, ex, ey + 2, x, y, k < 3 ? '#2e5634' : '#1c3a24'); }
        for (const [fx, fy] of [[0, 0], [2, -1], [-2, 1]]) { p.put(ex + fx, ey + fy, '#f4ead0'); p.put(ex + fx + 1, ey + fy, '#e8d8b0'); p.put(ex + fx, ey + fy + 1, '#ecc860'); }
      }
    }
    return p.done();
  };
  // along the verge in front of the track
  for (let x = 150; x < 384; x += 12 + Math.floor(r() * 10)) {
    const h = 9 + Math.floor(r() * 6), base = trackTop(x) + 13 + 6 + Math.floor(r() * 10);
    if (base > H + 2) continue;
    out.push({ c: leafSprite(17, h, 'fern', 100 + x), x: x - 8, y: Math.min(H, base) - h, k: 24 + Math.floor(r() * 12), ph: r(), amp: 1 });
  }
  // the planter
  out.push({ c: leafSprite(22, 30, 'frang', 7), x: 446, y: 222, k: 20, ph: 0.3, amp: 1 });
  for (const [x, h] of [[384, 26], [404, 30], [424, 24], [462, 22]]) out.push({ c: leafSprite(16, h, 'heli', x), x, y: 253 - h, k: 26 + (x % 7), ph: x / 97, amp: 1 });
  return out;
}
const UPLIGHTS = [216, 268, 344];                                // little lamps in the verge lighting the ferns from below
function drawPlants(t) {
  for (const ux of UPLIGHTS) { const uy = trackTop(ux) + 20; px(ctx, ux, uy, '#ffe6b0'); px(ctx, ux + 1, uy, '#c8a060'); halo(HL.bollard, ux, uy - 2, 0.7); }
  for (const q of PLANT_C) {
    const off = Math.round(q.amp * wave(t, q.k, q.ph) * 0.9 + 0.3 * wave(t, q.k * 3, q.ph * 2));
    ctx.drawImage(q.c, q.x + off, q.y);
  }
}
// what the puddles mirror: the big one holds the twin towers' crowns upside down (squashed, rippling), the other the
// moon and a lamp's warm light
function puddleSpan(q, y) {
  const cx = (q.x0 + q.x1) / 2, cy = (q.y0 + q.y1) / 2, rx = (q.x1 - q.x0) / 2, ry = (q.y1 - q.y0) / 2, d = 1 - ((y - cy) / ry) ** 2;
  return d <= 0 ? null : [Math.round(cx - rx * Math.sqrt(d)) + 1, Math.round(cx + rx * Math.sqrt(d)) - 1];
}
function drawPuddles(t, rain) {
  for (const q of PUDDLES) {
    if (q.mirror) {
      for (let y = q.y0 + 1; y < q.y1; y++) {
        const sp = puddleSpan(q, y);
        if (!sp) continue;
        const sy = TOW.tip - 2 + Math.round((q.y1 - 1 - y) * 2.4), off = Math.round(Math.sin(TAU * 45 * t / LOOP + y * 0.9) * (0.7 + rain));
        ctx.globalAlpha = 0.5 * (0.55 + 0.45 * (y - q.y0) / (q.y1 - q.y0));
        ctx.drawImage(TOWERS_C, sp[0] - off, sy, sp[1] - sp[0], 1, sp[0], y, sp[1] - sp[0], 1);
      }
      ctx.globalAlpha = 1;
      continue;
    }
    for (const [sx, col, a] of [[MOON.x, '#f6ecd2', 0.5], [LAMPS[1].x, '#ffd090', 0.4]]) {
      const sp = puddleSpan(q, Math.round((q.y0 + q.y1) / 2));
      if (!sp || sx < sp[0] + 2 || sx > sp[1] - 2) continue;
      for (let y = q.y0 + 1; y < q.y1; y++) {
        if (hash(sx, y, step(t, 720)) < 0.25) continue;
        ctx.globalAlpha = a; ctx.fillStyle = col;
        ctx.fillRect(sx - 1 + Math.round(Math.sin(y * 1.3 + TAU * 60 * t / LOOP) * 0.6), y, 2, 1);
      }
    }
  }
  ctx.globalAlpha = 1;
}
// rings where drops land in the puddles (the scene's own drizzle and its shower)
const RINGS = Array.from({ length: 16 }, (_, i) => ({ id: i, n: 60 + (i * 7) % 23 }));
function drawRings(t, dens) {
  ctx.fillStyle = '#9ab0d8';
  for (const g of RINGS) {
    if (g.id >= 16 * dens) continue;
    const { cyc, u } = cycle(t, g.n, g.id * 0.37), q = PUDDLES[hash(g.id, cyc, 3) < 0.75 ? 0 : 1];
    const y = Math.round(lerp(q.y0 + 3, q.y1 - 3, hash(g.id, cyc, 1))), sp = puddleSpan(q, y);
    if (!sp) continue;
    const x = Math.round(lerp(sp[0] + 3, sp[1] - 3, hash(g.id, cyc, 2))), rr = Math.round(u * 3);
    ctx.globalAlpha = 0.5 * (1 - u);
    ctx.fillRect(x - rr, y, 1, 1); ctx.fillRect(x + rr, y, 1, 1); if (rr > 1) { ctx.fillRect(x, y - 1, 1, 1); ctx.fillRect(x, y + 1, 1, 1); }
  }
  ctx.globalAlpha = 1;
}

/* ================= The park: a rain tree on the right, palms and trees on the far shore ================= */
const LEAF = ['#060e0c', '#0a1612', '#0e1e18', '#14281e', '#1c3426', '#28422c'];
function renderShoreTrees() {
  const p = painter(W, H);
  for (const [cx, cy, rx, ry] of [[104, 162, 12, 9], [118, 166, 9, 7], [92, 168, 10, 7], [446, 160, 14, 10], [468, 164, 12, 9], [430, 168, 8, 6]])
    for (let y = cy - ry; y <= cy + ry + 8; y++) for (let i = cx - rx; i <= cx + rx; i++) {
      const d = ((i - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 - 0.25 * Math.sin(i * 0.9) * Math.sin(y * 1.1);
      if (y > cy + 2 ? y < SHORE && Math.abs(i - cx) < rx * 0.9 : d < 1) p.put(i, y, d < 0.5 && y < cy - 2 ? '#183026' : y > cy + 3 ? '#0a1412' : '#10221c');
    }
  for (const [bx, top, lean] of [[112, 132, -1], [124, 138, 1], [438, 134, 1]]) {
    for (let y = top; y < SHORE; y++) { const x = bx + Math.round(lean * ((SHORE - y) / (SHORE - top)) ** 2 * 3); p.put(x, y, '#2a2a2a'); p.put(x + 1, y, '#141616'); }
    const tx = bx + lean * 3;
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * TAU + 0.3, len = 7 + (k % 3);
      for (let s = 1; s <= len; s++) {
        const fx = tx + Math.cos(a) * s, fy = top + Math.sin(a) * s * 0.55 + (s * s) / len * 0.45;
        p.put(fx, fy, s < 3 ? '#1e3a2c' : (k < 4 ? '#14281e' : '#1a3426'));
      }
    }
  }
  return p.done();
}
// the rain tree: a forked trunk lit warm on its left by an uplight at its foot (one canvas), and its wide feathery dome
// (another, cut into bands that sway each a little out of step with the next)
function renderTrunk() {
  const p = painter(W, H), T = TREE;
  for (let y = 118; y <= T.foot; y++) {
    const wt = Math.round(4 + Math.max(0, (y - 150) / 22) + (y > T.foot - 6 ? (y - T.foot + 6) * 0.8 : 0));
    for (let i = -wt; i <= wt; i++) {
      const f = (i + wt) / (2 * wt), streakv = hash(T.x + i, y >> 2, 31) < 0.3 ? -0.12 : 0;
      p.put(T.x + i, y, pick(['#1a1416', '#2a2020', '#3e3028', '#5a4434', '#86643e'], 0.85 - f * 0.9 + streakv + (y > 190 ? 0.1 : 0), T.x + i, y));
    }
  }
  for (const [ex, ey, w] of [[392, 104, 3], [416, 96, 3], [440, 92, 2], [478, 98, 3], [468, 112, 2]])
    for (let k = 0; k < w; k++) lineP(p.put, T.x - 2 + k, 130, ex + k, ey, k === 0 ? '#5a4434' : '#241c1c');
  for (let i = -9; i <= 9; i++) p.put(T.x + i, T.foot, '#14101a');
  return p.done();
}
const CANOPY = { x0: 350, y0: 32, y1: 126, bands: [32, 56, 78, 98, 126] };
function renderCanopy() {
  const p = painter(W - CANOPY.x0 + 4, CANOPY.y1 - CANOPY.y0), r = rng(5), T = TREE, ox = CANOPY.x0 - 2, oy = CANOPY.y0;
  const blobs = [];
  for (let k = 0; k < 46; k++) {
    const a = r() * Math.PI, rr = Math.sqrt(r());
    blobs.push({ x: 436 + Math.cos(a) * 74 * rr, y: 108 - Math.sin(a) * 62 * rr, rx: 7 + r() * 11, ry: 4 + r() * 6, s: r() });
  }
  for (let y = 34; y < 124; y++) for (let i = 352; i < W + 2; i++) {
    let v = -1, s = 0;
    for (const b of blobs) { const q = 1 - ((i - b.x) / b.rx) ** 2 - ((y - b.y) / b.ry) ** 2; if (q > v) { v = q; s = b.s; } }
    v += 0.3 * Math.sin(i * 1.7 + y * 0.9) * Math.sin(i * 0.6 - y * 2.1);
    if (v * 1.6 <= bayer(i, y) * 0.5 + 0.05) continue;
    const under = clamp((y - 92) / 30, 0, 1), sky = clamp(1 - (y - 34) / 50, 0, 1);
    const L = 0.18 + 0.4 * sky * (v < 0.35 ? 1 : 0.55) + 0.12 * s - 0.08 * under + (hash(i, y, 8) < 0.12 ? 0.18 : 0);
    let col = pick(LEAF, L, i, y);
    const warm = under * Math.max(0, 1 - Math.abs(i - T.x + 6) / 46);
    if (warm > 0.15 && hash(i, y, 9) < warm * 0.8) col = hash(i, y, 10) < 0.4 ? '#6a6438' : hash(i, y, 10) < 0.75 ? '#4a4c2c' : '#34381e';
    p.put(i - ox, y - oy, col);
  }
  return p.done();
}
function drawCanopy(t) {
  const B = CANOPY.bands, ox = CANOPY.x0 - 2;
  for (let b = 0; b < B.length - 1; b++) {
    const off = Math.round(wave(t, 20, b * 0.21) * 0.9 * (1 - b * 0.15) + 0.4 * wave(t, 52, b * 0.37));
    ctx.drawImage(CANOPY_C, 0, B[b] - CANOPY.y0, CANOPY_C.width, B[b + 1] - B[b], ox + off, B[b], CANOPY_C.width, B[b + 1] - B[b]);
  }
}

/* ================= The mamak: a 24-hour corner shop under the shophouse's five-foot way ================= */
// A two-storey shophouse: a parapet with a little pediment, louvred upper windows (two lit, one shut, an air
// conditioner under one), a lightbox signboard (RESTORAN BULAN, 24 JAM), and under it the five-foot way lit by
// fluorescent tubes: a menu lightbox, a TV on a bracket showing the football, the curry case, the tea counter with its
// urns, the roti griddle at the front, a ceiling fan; plastic stools and round steel tables spill onto the path.
const CREAM = ['#1e1a1a', '#2c2622', '#3c3229', '#4e4232', '#62533e', '#7a684c', '#94805e', '#ae9a74'];
const TUBES = [[10, 30], [42, 62], [76, 96]];                   // fluorescent tubes under the five-foot way's ceiling (y 146)
const TV = { x: 84, y: 151, w: 15, h: 9 };
const GRIDDLE = { x: 22, y: 194, w: 22 };                         // the roti griddle's plate (left end, top row)
const URNS = [88, 96];
const SIGN_LAMPS = [24, 86];                                       // gooseneck lamps over the signboard
const TUBE_FLICK = [[57.2, 58.4], [181.5, 182.3]];               // the middle tube stutters now and then
const TABLES = [{ x: 42, y: 210 }, { x: 82, y: 212 }, { x: 128, y: 209 }];   // table centre, top row
const STOOLS = [[33, 216, '#c8343a'], [52, 215, '#2a62c4'], [77, 220, '#2a9a58'], [93, 217, '#c8343a'], [118, 214, '#e8823a'], [139, 214, '#2a62c4']];
function renderMamak() {
  const p = painter(W, H), { x1, roof, sign, ceil, floor } = MAMAK, put = p.put;
  // the facade, lit from below by the sign and the tubes
  for (let y = roof; y < sign; y++) for (let i = 0; i <= x1; i++) {
    const v = 0.25 + 0.5 * (y - roof) / (sign - roof) + (i === x1 ? -0.2 : i === x1 - 1 ? 0.15 : 0);
    put(i, y, pick(CREAM, v, i, y));
  }
  // parapet: a coping, a pediment with a date plaque, the cornice over the upper floor
  for (let i = 0; i <= x1; i++) { put(i, roof, '#a8946c'); put(i, roof + 1, '#5a4c38'); put(i, roof + 6, '#8a7656'); put(i, roof + 7, '#2a2420'); }
  for (let k = 0; k < 9; k++) for (let i = 40 + k; i <= 70 - k; i++) put(i, roof - 1 - k, k === 8 || i === 40 + k ? '#b49e74' : i === 70 - k ? '#3e3428' : '#6a5a44');
  p.rect(47, roof - 7, 17, 7, '#3a3028'); signText(put, '1931', 48, roof - 6, '#c8b48a');
  // upper floor: three tall louvred windows between pilasters
  const WINX = [[10, 28], [44, 64], [80, 98]];
  WINX.forEach(([a, b], k) => {
    for (let y = 90; y <= 124; y++) for (let i = a; i <= b; i++) {
      const edge = i === a || i === b || y === 90 || y === 124;
      let col;
      if (edge) col = '#2a221c';
      else if (k === 1) col = (y % 3 === 0) ? '#1c3428' : (i === ((a + b) >> 1) ? '#14241c' : '#2a4a38');      // shut louvres
      else {
        const open = i < a + 4 || i > b - 4;                                    // shutters folded back either side
        col = open ? ((y % 3 === 0) ? '#1c3428' : '#305440') : pick(['#c07a3c', '#e09a50', '#ffc070', '#ffd890'], 0.35 + 0.6 * (y - 90) / 34, i, y);
      }
      put(i, y, col);
    }
    for (let i = a - 2; i <= b + 2; i++) { put(i, 125, '#a08a64'); put(i, 126, '#2a221c'); }   // sill
    for (let i = a; i <= b; i++) put(i, 89, '#8a7656');
  });
  // a lamp shade and a ceiling fan inside the left room, a figure's shoulder at the right one
  p.rect(17, 93, 5, 2, '#5a3a22'); put(19, 95, '#fff0c0');
  
  // pilasters with capitals
  for (const px0 of [3, 36, 72, 104]) for (let y = 86; y < 128; y++) { put(px0, y, '#9c8862'); put(px0 + 1, y, '#6e5e46'); put(px0 + 2, y, '#4a3e30'); }
  // an air conditioner under the middle window, a dark drip stain under it
  p.rect(48, 128, 11, 5, '#9aa0a2'); for (let i = 49; i < 58; i += 2) put(i, 130, '#5a6064'); for (let y = 133; y < 135; y++) put(56, y, '#3a3a40');
  // the signboard: a lightbox, red letters on cream, a green 24 JAM
  for (let y = sign; y < ceil; y++) for (let i = 1; i <= x1 - 1; i++)
    put(i, y, y === sign || y === ceil - 1 ? '#2a2420' : i === 1 || i === x1 - 1 ? '#3a3028' : y === ceil - 2 ? '#2e8a4e' : pick(['#d8ccb0', '#ece2c8', '#f6eedc'], 0.9 - Math.abs(i - 55) / 120, i, y));
  for (const lx of SIGN_LAMPS) { put(lx, sign - 4, '#3a3630'); put(lx, sign - 3, '#3a3630'); put(lx - 1, sign - 2, '#5a5448'); put(lx, sign - 2, '#5a5448'); put(lx + 1, sign - 2, '#5a5448'); }
  signText(put, 'RESTORAN BULAN', 6, sign + 3, '#c42a2a');
  signText(put, '24 JAM', x1 - 4 - signWidth('24 JAM'), sign + 3, '#1e7a3e');
  // the five-foot way: ceiling, the back wall in fluorescent white-green, tiles below
  for (let y = ceil; y < floor; y++) for (let i = 0; i <= x1 + 2; i++) {
    let col;
    if (y < ceil + 3) col = y === ceil ? '#1a1816' : '#3c3c38';
    else if (y < 176) col = pick(['#8ea89c', '#a4bcb0', '#b8cec2', '#c8dcd0'], 0.75 - (y - ceil) / 60 + (Math.abs(i - 55) < 40 ? 0.1 : 0), i, y);
    else if (y < 200) col = (y % 4 === 0 || i % 5 === 0) ? '#8aa094' : '#b2c6ba';
    else col = y === 200 ? '#8a8a76' : (i % 7 === 0) ? '#4e4c40' : '#6c6a58';
    put(i, y, col);
  }
  for (let i = 0; i <= x1 + 2; i++) { put(i, floor, '#2a2822'); put(i, floor + 1, '#1a1814'); }
  // the menu lightbox: three rows of dishes in little pictures, prices in red
  for (let y = 149; y <= 161; y++) for (let i = 26; i <= 76; i++) put(i, y, y === 149 || y === 161 || i === 26 || i === 76 ? '#26221e' : '#f2e8cc');
  const DISH = ['#d8782a', '#e8c040', '#4a9a4a', '#8a4a2a', '#f4f0e0', '#c83a2a'];
  for (let row = 0; row < 3; row++) for (let k = 0; k < 6; k++) {
    const dx = 28 + k * 8, dy = 151 + row * 3 + (row ? 1 : 0);
    p.rect(dx, dy, 3, 2, DISH[(k + row * 2) % 6]); put(dx + 4, dy, '#c42a2a'); put(dx + 5, dy, '#c42a2a');
  }
  // the TV's bracket and bezel (the picture is drawn per frame), a clock beside the menu
  p.rect(TV.x + 6, TV.y - 4, 2, 4, '#2a2a2a'); p.rect(TV.x - 1, TV.y - 1, TV.w + 2, TV.h + 2, '#141416');
  for (let y = -3; y <= 3; y++) for (let i = -3; i <= 3; i++) if (i * i + y * y <= 10) put(14 + i, 155 + y, i * i + y * y >= 7 ? '#3a3632' : '#f2f0e6');
  put(14, 153, '#1a1a1a'); put(14, 154, '#1a1a1a'); put(15, 155, '#1a1a1a'); put(16, 155, '#1a1a1a');
  // the curry case: warm trays behind glass on a steel stand
  for (let y = 168; y <= 188; y++) for (let i = 6; i <= 38; i++) {
    let col = y === 168 || y === 188 || i === 6 || i === 38 ? '#cfd6d4' : y < 172 ? '#e8eee8' : y > 184 ? '#9aa4a6' : '#fff2cc';
    if (y >= 174 && y <= 182 && i > 7 && i < 37) { const tr = Math.floor((i - 8) / 7); col = ['#d06a24', '#a8321e', '#e0b03a', '#5a8a3a'][tr % 4]; if (y === 174) col = mixHex(col, '#ffffff', 0.35); if ((i - 8) % 7 === 6) col = '#d8dcd8'; }
    put(i, y, col);
  }
  // the tea counter: a steel top, a ribbed front, two urns with taps, mugs, jars of biscuits with coloured lids
  for (let y = 180; y < 200; y++) for (let i = 48; i <= 104; i++) put(i, y, y === 180 ? '#eef2f2' : y === 181 ? '#aab4b6' : (i % 4 === 0) ? '#6a7476' : '#8a9496');
  for (const ux of URNS) for (let y = 166; y < 180; y++) for (let i = -3; i <= 3; i++) put(ux + i, y, y === 166 ? '#d8dee0' : i === -3 ? '#c8d0d2' : i === 3 ? '#5a6266' : i < 0 ? '#aab4b6' : '#7e888c');
  for (const ux of URNS) { put(ux - 4, 175, '#2a2a2a'); put(ux - 5, 175, '#2a2a2a'); put(ux - 5, 176, '#2a2a2a'); }
  for (let k = 0; k < 4; k++) { const jx = 50 + k * 6; p.rect(jx, 173, 4, 7, '#d8e4e0'); p.rect(jx, 172, 4, 1, ['#c8343a', '#e8c040', '#2a62c4', '#2a9a58'][k]); p.rect(jx + 1, 175, 2, 4, ['#d89a5a', '#e8d090', '#b8703a', '#f0e0b0'][k]); }
  for (let k = 0; k < 5; k++) { const mx = 72 + k * 3; p.rect(mx, 177, 2, 3, '#f0f0ea'); put(mx, 177, '#c8a070'); }
  // square columns at the corner and the left edge
  for (const [a, b] of [[0, 3], [x1 - 4, x1 + 1]]) for (let y = ceil; y < floor; y++) for (let i = a; i <= b; i++)
    put(i, y, i === a ? '#d2c8ac' : i === b ? '#6a6250' : y > floor - 5 ? '#5a5444' : '#aea48a');
  return p.done();
}
// the roti griddle, drawn over the roti man who stands behind it
function renderGriddle() {
  const p = painter(W, H), put = p.put, floor = MAMAK.floor;
  // the roti griddle: a black round plate on a steel cabinet, a roti on it, a tin of ghee
  const G = GRIDDLE;
  for (let y = G.y + 2; y < floor; y++) for (let i = G.x - 1; i <= G.x + G.w; i++) put(i, y, i === G.x - 1 ? '#b8c0c2' : i === G.x + G.w ? '#4a5254' : (y % 5 === 0) ? '#6a7274' : '#8a9294');
  for (let i = G.x; i < G.x + G.w; i++) { put(i, G.y, '#1a1816'); put(i, G.y + 1, '#2c2622'); }
  for (let i = G.x + 4; i < G.x + 12; i++) { put(i, G.y - 1, '#f0d290'); put(i, G.y, '#c89848'); }
  p.rect(G.x + G.w - 6, G.y - 3, 4, 3, '#c8c0a0'); put(G.x + G.w - 5, G.y - 3, '#e8e0c0');
  return p.done();
}
// the tables and stools out on the path, between the diners behind them and those in front
function renderTables() {
  const p = painter(W, H), put = p.put;
  // round steel tables on a pedestal, plastic stools round them
  for (const T of TABLES) {
    for (let i = -7; i <= 7; i++) { put(T.x + i, T.y, Math.abs(i) > 5 ? '#9aa4a6' : '#d8e0e2'); put(T.x + i, T.y + 1, '#6a7476'); }
    for (let i = -6; i <= 6; i++) put(T.x + i, T.y - 1, '#eef2f2');
    for (let y = T.y + 2; y < T.y + 9; y++) put(T.x, y, '#5a6264');
    for (let i = -3; i <= 3; i++) put(T.x + i, T.y + 9, '#3a4244');
  }
  for (const [sx, sy, col] of STOOLS) {
    for (let i = -2; i <= 2; i++) { put(sx + i, sy, mixHex(col, '#ffffff', 0.2)); put(sx + i, sy + 1, col); }
    for (let y = sy + 2; y < sy + 5; y++) { put(sx - 2, y, mixHex(col, '#000000', 0.35)); put(sx + 2, y, mixHex(col, '#000000', 0.35)); }
  }
  return p.done();
}
// the tubes, the TV's match (a goal at GOAL: the picture cuts to the crowd going wild) and the fan's blades
function drawMamakLights(t) {
  TUBES.forEach(([a, b], i) => {
    let on = 1;
    if (i === 1 && inEvent(t, TUBE_FLICK)) on = hash(3, Math.floor(t * 20)) < 0.5 ? 0.3 : 1;
    ctx.fillStyle = on > 0.5 ? '#f4fff8' : '#8a9a94';
    ctx.fillRect(a, MAMAK.ceil + 2, b - a + 1, 1);
  });
  if (t >= GOAL && t < GOAL + 3) {
    for (let y = 0; y < TV.h; y++) for (let x = 0; x < TV.w; x++) {
      const h = hash(x >> 1, y, step(t, 2400));
      ctx.fillStyle = y < 3 ? '#1a2a4a' : h < 0.3 ? '#e8e0d0' : h < 0.55 ? '#c83a3a' : h < 0.7 ? '#e8c040' : '#3a3a52';
      ctx.fillRect(TV.x + x, TV.y + y, 1, 1);
    }
  } else {
    for (let y = 0; y < TV.h; y++) { ctx.fillStyle = y % 3 === 0 ? '#3a9a48' : '#2c8240'; ctx.fillRect(TV.x, TV.y + y, TV.w, 1); }
    const pan = Math.round(wave(t, 16) * 2);
    ctx.fillStyle = '#e8eef0'; ctx.fillRect(TV.x + (TV.w >> 1) + pan, TV.y, 1, TV.h);
    for (let k = 0; k < 6; k++) {
      ctx.fillStyle = k < 3 ? '#e83a3a' : '#f2f2f2';
      const px0 = Math.round(TV.x + 1 + ((k * 4.7 + wave(t, 40 + k * 7, k * 0.13) * 3 + pan + 20) % (TV.w - 2)));
      ctx.fillRect(px0, TV.y + 2 + (k * 3) % (TV.h - 3), 1, 2);
    }
    ctx.fillStyle = '#ffffff'; ctx.fillRect(TV.x + 2 + Math.round((wave(t, 60) * 0.5 + 0.5) * (TV.w - 5)), TV.y + 4 + Math.round(wave(t, 44) * 2), 1, 1);
  }
  // the fan: a hub and two blades, turning
  const a = step(t, 4800) % 4;
  ctx.fillStyle = '#4a4a46'; ctx.fillRect(56, MAMAK.ceil + 3, 1, 3);
  ctx.fillStyle = '#6a6a64';
  if (a === 0 || a === 2) ctx.fillRect(50, MAMAK.ceil + 6, 13, 1); else { ctx.fillRect(53, MAMAK.ceil + 5, 7, 1); ctx.fillRect(54, MAMAK.ceil + 7, 5, 1); }
  ctx.fillStyle = '#2a2a28'; ctx.fillRect(55, MAMAK.ceil + 6, 3, 1);
}
// upstairs and on the roof: laundry on a pole out of the left window, a TV's flicker through the middle shutters, the
// neighbour pacing on the phone in the right window, a cat on the air conditioner, mynas on the parapet, moths round
// the sign's lamps
function drawMamakUpper(t, crowd) {
  const S = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
  // laundry: a pole from the left window's sill, a shirt, a sarong and a towel lifting in the breeze
  S(0, 96, 11, 1, '#6a5a44');
  [[1, '#c8d8e8', 7], [5, '#a83a4a', 11], [8, '#e8c060', 6]].forEach(([x, col, h], i) => {
    const sw = Math.round(wave(t, 24 + i * 6, i * 0.31) * 0.8 + 0.3);
    for (let y = 0; y < h; y++) S(x + (y > h / 2 ? sw : 0), 97 + y, 3, 1, y === 0 ? mixHex(col, '#ffffff', 0.2) : y === h - 1 ? mixHex(col, '#000000', 0.3) : col);
  });
  // the TV's light through the middle window's louvres
  const tvc = ['#5a8cff', '#8ab4ff', '#3a5ac8', '#c8d8ff'][Math.floor(hash(7, step(t, 360)) * 4)];
  ctx.globalAlpha = 0.45; ctx.fillStyle = tvc;
  for (let y = 92; y < 124; y++) if (y % 3 !== 0) ctx.fillRect(47, y, 15, 1);
  ctx.globalAlpha = 1;
  // the neighbour pacing on the phone behind the right window (head and shoulders, clipped to the glass)
  if (crowd > 0) {
    const nx = Math.round(89 + wave(t, 6, 0.2) * 4), ny = 106 + (Math.round(wave(t, 24)) > 0 ? 1 : 0);
    S(Math.max(82, nx - 4), ny + 6, Math.min(8, 97 - Math.max(82, nx - 4)), 124 - ny - 6, '#3a2a22');
    S(nx - 2, ny, 4, 5, '#4a3428'); S(nx - 2, ny, 4, 1, '#2a1c18'); S(nx + 2, ny + 2, 1, 3, '#3a2a22');
  }
  // the cat on the air conditioner, its tail swinging down the front
  const tail = Math.round(wave(t, 40) * 1.4);
  S(51, 124, 6, 4, '#1e1a1c'); S(55, 121, 3, 3, '#1e1a1c'); S(55, 120, 1, 1, '#1e1a1c'); S(57, 120, 1, 1, '#1e1a1c'); S(56, 122, 1, 1, '#b8c060');
  S(51 + tail, 128, 1, 3, '#1e1a1c'); S(50 + tail * 2, 131, 1, 2, '#1e1a1c');
  // mynas on the parapet: two hop along now and then, one preens
  for (const [bx0, ph] of [[18, 0.1], [26, 0.45], [92, 0.7]]) {
    const hop = Math.floor(frac(t / 20 + ph) * 4), x = bx0 + [0, 2, 3, 1][hop], up = frac(t / 5 + ph) < 0.06 ? 1 : 0;
    S(x, MAMAK.roof - 3 - up, 3, 2, '#2a2018'); S(x + 2, MAMAK.roof - 4 - up, 2, 2, '#1a1410'); S(x + 4, MAMAK.roof - 3 - up, 1, 1, '#e8c040');
    S(x, MAMAK.roof - 1 - up, 1, 1, '#e8c040');
  }
  // the sign's two lamps and the moths circling them
  for (const lx of SIGN_LAMPS) {
    S(lx - 1, MAMAK.sign - 1, 3, 1, '#fff0c8');
    halo(HL.signLamp, lx, MAMAK.sign, 0.9);
    for (let m = 0; m < 3; m++) {
      const a = TAU * ((13 + m * 7) * t / LOOP + m * 0.33 + lx * 0.01), rr = 3 + m;
      S(Math.round(lx + Math.cos(a) * rr), Math.round(MAMAK.sign - 3 + Math.sin(a) * rr * 0.6), 1, 1, '#e8dcc0');
    }
  }
}
// the nearest table and the shop's things on the wet pavement, nearest the viewer: a table with a plate of roti canai,
// a bowl of dhal and two glasses of teh tarik, empty stools, a stack of stools, a cooler box with a jug, a drain grate
const NEAR_TABLE = { x: 86, y: 241 };
function renderNear() {
  const p = painter(W, H), put = p.put, T = NEAR_TABLE;
  for (let i = -11; i <= 11; i++) { put(T.x + i, T.y, Math.abs(i) > 8 ? '#a8b2b4' : '#dfe6e8'); put(T.x + i, T.y + 1, '#78848a'); put(T.x + i, T.y + 2, '#4a5458'); }
  for (let i = -10; i <= 10; i++) put(T.x + i, T.y - 1, '#f0f4f4');
  for (let y = T.y + 3; y < T.y + 16; y++) { put(T.x, y, '#6a7476'); put(T.x + 1, y, '#3a4244'); }
  for (let i = -5; i <= 6; i++) put(T.x + i, T.y + 16, '#2a3234');
  // roti canai on a plate, dhal in a bowl, two glasses of tea with their foam
  for (let i = -3; i <= 3; i++) { put(T.x - 3 + i, T.y - 2, '#e8ecee'); }
  for (let i = -2; i <= 2; i++) put(T.x - 3 + i, T.y - 3, (i & 1) ? '#c88a40' : '#ecc888');
  put(T.x + 3, T.y - 3, '#e0b040'); put(T.x + 4, T.y - 3, '#e8c060'); put(T.x + 3, T.y - 2, '#c8d0d2'); put(T.x + 4, T.y - 2, '#c8d0d2');
  for (const gx of [T.x + 7, T.x - 9]) { p.rect(gx, T.y - 5, 2, 4, '#c08a50'); p.rect(gx, T.y - 5, 2, 1, '#f4e4c4'); put(gx + 2, T.y - 4, '#d8dcdc'); }
  // two empty stools and a stack of five against the corner pillar
  for (const [sx, sy, col] of [[70, 249, '#2a62c4'], [104, 251, '#c8343a']]) {
    for (let i = -3; i <= 3; i++) { put(sx + i, sy, mixHex(col, '#ffffff', 0.2)); put(sx + i, sy + 1, col); }
    for (let y = sy + 2; y < sy + 7; y++) { put(sx - 3, y, mixHex(col, '#000000', 0.35)); put(sx + 3, y, mixHex(col, '#000000', 0.35)); }
  }
  for (let k = 0; k < 6; k++) for (let i = -3; i <= 3; i++) { put(124 + i, 228 + k * 3, '#e0503c'); put(124 + i, 229 + k * 3, '#b83a2c'); put(124 + i, 230 + k * 3, i === -3 || i === 3 ? '#8a2a20' : '#9a3226'); }
  // a blue cooler box with a jug of iced water on its lid
  for (let y = 236; y < 249; y++) for (let x = 10; x <= 30; x++) put(x, y, y === 236 ? '#6a9ad0' : y < 239 ? '#2e64a8' : x === 10 ? '#3a74b8' : x === 30 ? '#162e62' : '#224c8e');
  for (let x = 9; x <= 32; x++) put(x, 249, '#08080c');
  p.rect(12, 240, 17, 1, '#e8eef4'); p.rect(26, 230, 4, 6, '#d8e8f0'); p.rect(26, 230, 4, 1, '#ffffff'); p.rect(27, 232, 2, 3, '#e86a3a');
  // the drain along the kerb
  for (let x = 0; x < 140; x++) { put(x, 262, '#0a0a0e'); put(x, 263, x % 3 === 0 ? '#2a2a30' : '#121216'); put(x, 264, '#0a0a0e'); }
  return p.done();
}
// steam from the griddle, the urns and the tea on the near table
const STEAM = [];
{ const r = rng(61); for (let i = 0; i < 26; i++) STEAM.push({ src: i % 3, n: 40 + Math.floor(r() * 16), ph: r(), k: 20 + Math.floor(r() * 20), p: r() }); }
const STEAM_SRC = [[GRIDDLE.x + 8, GRIDDLE.y - 1, 16], [URNS[0], 165, 10], [NEAR_TABLE.x + 7, NEAR_TABLE.y - 6, 12]];
function drawSteam(t) {
  ctx.fillStyle = '#e8f0ee';
  for (const s of STEAM) {
    const [sx, sy, rise] = STEAM_SRC[s.src], u = frac(t * s.n / LOOP + s.ph);
    const x = sx + Math.sin(TAU * (s.k * t / LOOP + s.p)) * (1 + u * 2) + (s.p - 0.5) * 4, y = sy - u * rise;
    ctx.globalAlpha = 0.35 * (1 - u) * (u < 0.15 ? u / 0.15 : 1);
    ctx.fillRect(Math.round(x), Math.round(y), u > 0.5 ? 2 : 1, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= People ================= */
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#f0c8a0', '#d8a47a', '#b47e56', '#86583a'], HAIRS = ['#16100e', '#2c1c14', '#4a2e1c', '#6a4a2e', '#c8c4c0'];
const NIGHT = '#0c1222';
/** a look: proportions for a height of h px, colours dimmed for the night (dk), a lit line from `glow` on the shoulders */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.18, m = c => mixHex(c, NIGHT, dk), glow = o.glow ?? '#dfe6f4';
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.36);
  return { h, hh, hw: h >= 18 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 18 ? 6 : h >= 14 ? 5 : 4, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], glow, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, glow, 0.4), bot: m(bot), shoe: m('#1c181c'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, bag: o.bag ? m(o.bag) : null, scarf: o.scarf ? m(o.scarf) : null, apron: o.apron ? m(o.apron) : null };
}
/**
 * One person, feet on row fy, centred on column x; returns the head's top row and where the hands are.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated with the
 * seat on row fy; o.kneel; o.aL / o.aR: 0 down, 1 up, 2 bent up with a phone, 3 out, 4 bent up (a cup), 5 to the mouth
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : o.kneel ? (P.ll >> 1) + 1 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  const hands = [];
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.kneel) { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x + 1 : x - 4, fy, 4, 1, P.bot); }
      else if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
    } else if (o.kneel) {
      R(tx, ly, bw, legs - 1, P.bot); R(tx - 1, fy, bw + 2, 1, P.bot);
    } else {
      const lw = bw >= 6 ? 2 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
      if (P.skirt) R(tx - (P.skirtW || 0), ly, bw, (legs >> 1) + 2, P.skirt);
    }
  } else if (o.sit && view === 1) {
    R(tx, fy + 1, 2, 4, legC); R(tx + bw - 2, fy + 1, 2, 4, legC); R(tx, fy + 5, 2, 1, P.shoe); R(tx + bw - 2, fy + 5, 2, 1, P.shoe);
  } else if (o.sit && side) {
    R(dir > 0 ? x - 1 : x - 3, fy - 1, 5, 2, P.bot); R(dir > 0 ? x + 2 : x - 3, fy + 1, 2, 4, legC); R(dir > 0 ? x + 2 : x - 4, fy + 5, 3, 1, P.shoe);
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (P.apron && view !== 0) R(tx + (side ? 0 : 1), ty + 2, side ? bw : bw - 2, th - 2 + (legs ? 3 : 0), P.apron);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);
  if (P.bag && view === 0) R(tx + 1, ty + 1, bw - 2, th - 2, P.bag);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); hands.push([ax, ty + th - 1]); }
    else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); hands.push([ax, hy - 3]); }
    else if (st === 2 || st === 4) {
      R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin); hands.push([ax, hy + 1]);
      if (st === 2) { const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#e8f0ff'); R(p0, hy - 1, 2, 1, '#7ad0f0'); } }
    } else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); hands.push([sg < 0 ? ax - 3 : ax + 3, ty + 1]); }
    else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); hands.push([ax + (sg < 0 ? 1 : -1), hy + hh - 2]); }
  };
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, k < 2 ? P.topD : P.skin); R(x + dir * 4 - (dir < 0 ? 1 : 0), ty - 4, 2, 3, '#0e1018'); R(x + dir * 4 - (dir < 0 ? 1 : 0), ty - 4, 2, 1, '#bfe8ff'); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); hands.push([x + dir * 4, ty + 2]); }
    else if (st === 5) { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.topD); R(x + dir * 2, hy + hh - 1, 1, 1, P.skin); hands.push([x + dir * 2, hy + hh - 1]); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); hands.push([x + dir * 2, ty]); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) { R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL); if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin); }
  else if (view === 1) { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); } }
  else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.scarf) { R(hx - (side ? 0 : 1), hy - 1, hw + (side ? 1 : 2), hh + 1, P.scarf); if (view !== 0) R(hx + (side && dir < 0 ? 0 : 1), hy + 1, hw - (side ? 1 : 2), hh - 1, P.skin); R(hx - 1, hy + hh, hw + 2, 2, P.scarf); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); }
  return { hy, ty, hands };
}
const TOPS = ['#c8404a', '#e8c040', '#ece6da', '#3a6ac8', '#2a9a8a', '#e87aa8', '#e8823a', '#4a9a4a', '#8a5ac8', '#c8d0e0', '#5ab0d8', '#f4f2ee'];
const BOTS = ['#33466e', '#22202c', '#b09a70', '#d8d4cc', '#3a3a58', '#4a3a30'];
/** an umbrella held over a head whose top is on row hy: a dome w wide, a rib line, the stick down to the hand */
function umbrella(x, hy, w, col, open = 1) {
  if (open <= 0) return;
  const hw = Math.max(2, Math.round((w >> 1) * open)), top = hy - 5;
  for (let k = 0; k < 3; k++) { const ww = hw - (k === 0 ? 2 : k === 1 ? 1 : 0); R(x - ww, top + k, ww * 2 + 1, 1, k === 0 ? mixHex(col, '#ffffff', 0.25) : k === 2 ? mixHex(col, '#000000', 0.3) : col); }
  for (let i = -hw; i <= hw; i += 2) R(x + i, top + 3, 1, 1, mixHex(col, '#000000', 0.4));
  R(x, top + 3, 1, 4, '#2a2a30');
}
const rankOf = i => frac((i + 0.5) * 0.618);                    // spread evenly over a group (SCENE_GUIDE, The crowd)
const CROWD = 0.7;
const SHOPLIT = '#e8fff4';                                       // the tubes' cool light on shoulders under the five-foot way
const UMB = ['#2a62c4', '#c8343a', '#2a8a5a', '#e8a030', '#6a4ab0', '#3a3a44'];
const umbOpen = t => clamp((env(t, SHOWER[0], SHOWER[1], 3) - 0.3) / 0.4, 0, 1);

/* ----- the mamak's people: the roti man behind his griddle, the tea puller at the counter (rank 0, the cast) ----- */
const ROTI = look(20, 2, 0, '#2a3e66', BOTS[1], { glow: SHOPLIT, dk: 0.05, hat: '#f4f4ee', apron: '#e8e8e0' });
const TARIK = look(21, 3, 0, '#f4f2ee', '#3a5a4a', { glow: SHOPLIT, dk: 0.05, apron: '#5a6a70' });
// the roti man spins his dough over the griddle every 8 s, slaps it down, works it, flips it; steam rises off the plate
function drawRoti(t) {
  const x = 32, u = (t % ROTI_EVERY) / ROTI_EVERY, spin = u < 0.3, flip = u > 0.72 && u < 0.8;
  const s = person(x, MAMAK.floor, ROTI, { view: 1, aL: spin ? 4 : 3, aR: spin ? 4 : 3 });
  if (spin) {                                                     // the dough spun thin above his head, widening as it turns
    const w = 3 + Math.round(u / 0.3 * 6), y = s.hy - 3 - Math.round(Math.sin(u / 0.3 * Math.PI) * 2), ph = step(t, 960) & 1;
    R(x - w, y, w * 2 + 1, 1, ph ? '#f2dca8' : '#e8cc90'); R(x - w + 1, y - 1, w * 2 - 1, 1, '#fbecc8');
  } else if (flip) R(x - 6, MAMAK.floor - 14, 8, 1, '#f0d290');
  return s;
}
// the pull: every 6 s he lifts the mug high and pours a long ribbon into the jug at his hip, then pours it back
function drawTarik(t) {
  const x = 66, pause = t >= GOAL && t < GOAL + 3.5, u = (t % TARIK_EVERY) / TARIK_EVERY;
  const pulling = !pause && u < 0.62, back = !pause && u >= 0.62 && u < 0.8;
  const s = person(x, MAMAK.floor, TARIK, { view: 1, aL: pulling ? 1 : 4, aR: 3 });
  const [hx0, hy0] = s.hands[0], [hx1, hy1] = s.hands[1];
  const lift = pulling ? Math.round(smooth(0, 0.25, u) * 3) : 0;
  R(hx0 - 1, hy0 - 3 - lift, 3, 3, '#e8e8e4'); R(hx0 - 1, hy0 - 3 - lift, 3, 1, '#ffffff');       // the mug
  const jx = hx1 + 1, jy = hy1 + 2;
  R(jx - 1, jy, 4, 4, '#c8d0d2'); R(jx - 1, jy, 4, 1, '#f2e2c0'); R(jx + 3, jy + 1, 1, 2, '#9aa4a6');   // the jug
  if (pulling && u > 0.04) {
    const n = 18, fl = step(t, 1440);
    for (let k = 0; k <= n; k++) {
      const v = k / n, xx = lerp(hx0, jx + 1, v) + Math.sin(v * Math.PI) * 1.5, yy = lerp(hy0 - 1 - lift, jy - 1, v);
      R(Math.round(xx), Math.round(yy), 1, 1, (k + fl) % 4 === 1 ? '#f6deb0' : '#d8a060');
    }
  } else if (back) { R(hx0, hy0 + 1, 1, 3, '#d8a060'); }
}

/* ----- diners round the steel tables (the grey shirt watching the TV leads the cheer for the goal) ----- */
const DINERS = [
  { x: 36, y: 213, P: look(20, 1, 0, TOPS[3], BOTS[0], { glow: SHOPLIT, dk: 0.12 }), o: { view: 2, dir: 1, sit: true }, eat: 6, r: rankOf(0) },
  { x: 49, y: 212, P: look(20, 0, 2, TOPS[5], BOTS[1], { glow: SHOPLIT, dk: 0.12, scarf: '#d86a8a' }), o: { view: 2, dir: -1, sit: true }, eat: 8, r: rankOf(0) },
  { x: 82, y: 207, P: look(20, 2, 1, TOPS[7], BOTS[1], { glow: SHOPLIT, dk: 0.14 }), o: { view: 1, sit: true, aL: 3 }, eat: 10, behind: true, r: rankOf(1) },
  { x: 77, y: 219, P: look(22, 0, 0, TOPS[9], BOTS[0], { glow: SHOPLIT, dk: 0.2 }), o: { view: 0, sit: true }, eat: 12, goal: true, r: 0 },
  { x: 119, y: 211, P: look(20, 3, 0, TOPS[6], BOTS[2], { glow: '#dfe6f4', dk: 0.18 }), o: { view: 2, dir: 1, sit: true }, eat: 8, r: rankOf(2) },
  { x: 137, y: 211, P: look(20, 1, 3, TOPS[10], BOTS[4], { glow: '#dfe6f4', dk: 0.18, long: 3 }), o: { view: 2, dir: -1, sit: true, aR: 2 }, eat: 0, r: rankOf(3) },
];
function drawDiner(d, t) {
  const cheer = t >= GOAL && t < GOAL + 2.6, o = { ...d.o };
  const ph = d.eat ? (t + d.x * 0.37) % d.eat : 9;
  if (cheer && (d.goal || d.o.view === 2)) { o.aL = 1; o.aR = 1; if (o.view === 2) o.aR = 1; }
  else if (d.eat && ph < 1.4) o.aR = d.o.view === 2 ? 5 : 4;      // a bite of roti, a sip of tea
  else if (d.eat && d.o.view === 2) o.aR = 3;
  const s = person(d.x, d.y, d.P, o);
  if (!cheer && d.eat && ph < 1.4) { const [hx, hy] = s.hands.at(-1) ?? [d.x, s.ty]; R(hx, hy - 1, 2, 2, '#e0b070'); }
}
function drawDinersBehind(t, crowd) { for (const d of DINERS) if (d.behind && d.r < crowd) drawDiner(d, t); }
function drawDinersFront(t, crowd) {
  for (const T of TABLES) { R(T.x - 4, T.y - 3, 2, 3, '#c89458'); R(T.x - 4, T.y - 3, 2, 1, '#f2e2c0'); R(T.x + 1, T.y - 1, 5, 1, '#e8d8a8'); R(T.x + 2, T.y - 2, 3, 1, '#d8b070'); }
  for (const d of DINERS) if (!d.behind && d.r < crowd) drawDiner(d, t);
}

/* ----- the people who stay on the lakeside path ----- */
const PATHERS = [
  { id: 'photo', x: 204, y: FEET, P: look(18, 2, 0, TOPS[2], '#4a6a9a', { dk: 0.2 }), r: rankOf(2) },
  { id: 'dad', x: 262, y: FEET, P: look(19, 1, 0, TOPS[3], BOTS[2], { dk: 0.2 }), r: rankOf(3), umb: UMB[0] },
  { id: 'son', x: 270, y: FEET, P: look(12, 0, 0, TOPS[1], BOTS[0], { dk: 0.2 }), r: rankOf(3) },
  { id: 'him', x: 333, y: 202, P: look(19, 2, 0, TOPS[4], BOTS[1], { dk: 0.22 }), r: rankOf(0) },
  { id: 'her', x: 341, y: 202, P: look(18, 0, 1, TOPS[5], BOTS[3], { dk: 0.22, long: 3 }), r: rankOf(0), umb: UMB[1] },
  { id: 'selfie', x: 398, y: FEET, P: look(19, 0, 0, TOPS[0], BOTS[3], { dk: 0.22, long: 2 }), r: rankOf(1), umb: UMB[4] },
];
const inShow = t => SHOWS.some(s => t >= s.rise && t < s.fall);
function pathPose(q, t) {
  const show = inShow(t), u = umbOpen(t);
  switch (q.id) {
    case 'photo': return show ? { view: 0, kneel: true, aL: 4, aR: 2 } : { view: 0, kneel: true, aR: 3 };
    case 'dad': return { view: 0, aR: u > 0 ? 4 : 0, aL: 0 };
    case 'son': return { view: 0, aL: show && (t % 10) < 4 ? 1 : 0 };
    case 'him': return { view: 0, sit: true, lean: 1, aR: u > 0 ? 4 : 0 };
    case 'her': return { view: 0, sit: true, lean: (t % 40) < 14 ? -1 : 0 };
    default: return show && u <= 0 ? { view: 0, aL: 2 } : { view: 0, aL: u > 0 ? 4 : 0 };
  }
}
function drawBench(x, y) {
  R(x - 2, y - 2, 22, 2, '#4a3a2e'); R(x - 2, y - 2, 22, 1, '#8a6a4a'); R(x - 1, y, 2, 4, '#2a2420'); R(x + 16, y, 2, 4, '#2a2420');
  R(x - 2, y - 8, 22, 1, '#6a523c'); R(x - 2, y - 6, 22, 1, '#4a3a2e');
}

/* ----- walkers crossing the path and joggers on the track, each a whole number of times a loop ----- */
const WALKERS = [
  { n: 3, dir: -1, ph: 0.12, P: look(19, 1, 2, '#8a5ac8', '#5a7aaa', { dk: 0.22, long: 2, bag: '#c8a060' }), umb: UMB[3], r: rankOf(0) },
  { n: 2, dir: 1, ph: 0.55, P: look(18, 3, 4, '#c8d0e0', BOTS[2], { dk: 0.24 }), umb: UMB[5], r: rankOf(1), mate: look(17, 3, 4, '#b86a7a', '#6a5a7a', { dk: 0.24, scarf: '#4a6a8a', skirt: '#4a5a7a' }) },
  { n: 3, dir: 1, ph: 0.8, P: look(19, 2, 0, '#e8e2d4', '#6a6a7a', { dk: 0.22 }), umb: UMB[0], r: rankOf(2) },
];
const JOGGERS = [
  { n: 6, dir: -1, ph: 0.3, P: look(22, 1, 0, '#e8823a', BOTS[1], { dk: 0.28 }), r: rankOf(0) },
  { n: 5, dir: 1, ph: 0.62, P: look(21, 0, 1, '#3ab0d8', BOTS[1], { dk: 0.28, long: 2 }), r: rankOf(1) },
];
function walkerX(w, t) { const u = frac(t * w.n / LOOP + w.ph); return w.dir > 0 ? lerp(112, 492, u) : lerp(492, 112, u); }
function drawWalkers(t, crowd) {
  const st = step(t, 768) & 1, u = umbOpen(t);
  for (const w of WALKERS) {
    if (w.r >= crowd) continue;
    const x = walkerX(w, t);
    if (x < 114) continue;
    const s = person(x, FEET + 1, w.P, { view: 2, dir: w.dir, step: st, aR: u > 0 ? 4 : 0 });
    if (w.mate) person(x - 7 * w.dir, FEET + 1, w.mate, { view: 2, dir: w.dir, step: st ^ 1 });
    umbrella(Math.round(x + w.dir), s.hy, 11, w.umb, u);
  }
}
function drawJoggers(t, crowd) {
  const st = step(t, 1440) & 1;
  for (const j of JOGGERS) {
    if (j.r >= crowd) continue;
    const u = frac(t * j.n / LOOP + j.ph), x = j.dir > 0 ? lerp(120, 494, u) : lerp(494, 120, u), fy = trackTop(Math.round(x)) + 9;
    if (fy > H + 24) continue;
    person(x, fy, j.P, { view: 2, dir: j.dir, step: st, aR: st ? 3 : 0 });
  }
}

/* ----- a delivery rider waiting on his kapcai by the mamak, a box on the rack (the bike stays when he goes) ----- */
const RIDER = look(20, 2, 0, '#3a8a5a', BOTS[1], { dk: 0.18, glow: SHOPLIT });
const RIDER_R = rankOf(0);
function drawRider(t, crowd) {
  const bx = 150, by = 234;
  R(bx - 3, by - 5, 5, 5, '#141418'); R(bx - 2, by - 4, 3, 3, '#3a3a40'); R(bx + 17, by - 5, 5, 5, '#141418'); R(bx + 18, by - 4, 3, 3, '#3a3a40');
  R(bx, by - 9, 18, 3, '#c8d0d8'); R(bx + 2, by - 10, 10, 2, '#2a2a30'); R(bx + 14, by - 12, 3, 5, '#8a96a8'); R(bx + 15, by - 14, 4, 1, '#3a3a40');
  R(bx + 4, by - 6, 9, 2, '#5a6270'); R(bx + 19, by - 10, 2, 2, '#fff2c0');
  R(bx - 4, by - 18, 9, 8, '#c84a2a'); R(bx - 4, by - 18, 9, 1, '#e87a4a'); R(bx - 3, by - 15, 7, 1, '#8a2e1a');
  R(bx - 5, by, 30, 1, '#0a0a10');
  if (RIDER_R >= crowd) return;
  const s = person(bx + 9, by - 10, RIDER, { view: 2, dir: 1, sit: true, aR: 2 });
  R(bx + 7, s.hy - 1, 5, 3, '#e8e8ea'); R(bx + 7, s.hy + 1, 5, 1, '#3a4a5a');
  if ((t % 30) < 1.2) R(bx + 13, s.ty - 4, 2, 1, '#ffffff');      // his phone pings with an order now and then
}
// a black cat by the tables, its tail flicking
function drawCat(t) {
  const x = 104, y = 226, f = (t % 7.5) < 0.8 ? 1 : 0;
  R(x, y - 3, 5, 3, '#2a2420'); R(x + 4, y - 5, 3, 3, '#2a2420'); R(x + 4, y - 6, 1, 1, '#2a2420'); R(x + 6, y - 6, 1, 1, '#2a2420');
  R(x + 5, y - 4, 1, 1, '#c8d060'); R(x - 2, y - 4 - f, 2, 1, '#2a2420'); R(x - 3, y - 5 - f, 1, 1, '#2a2420'); R(x, y, 6, 1, '#0a0a10');
}
function drawShopPeople(t, crowd) {
  if (crowd > 0) drawRoti(t);
  ctx.drawImage(GRID_C, 0, 0);
  if (crowd > 0) drawTarik(t);
}
function drawPathPeople(t, crowd) {
  const u = umbOpen(t);
  drawBench(330, 204);
  for (const q of PATHERS) {
    if (q.r >= crowd) continue;
    const s = person(q.x, q.y, q.P, pathPose(q, t));
    if (q.umb) umbrella(q.id === 'her' ? q.x - 4 : q.x + 1, s.hy, q.id === 'her' ? 17 : 11, q.umb, u);
  }
  drawWalkers(t, crowd);
}
// the people along the esplanade across the lake: small dark figures against the mall's lights, a few strolling
const FAR_FOLK = [];
{ const r = rng(19); let i = 0; for (let x = MALL.x0 + 6; x < MALL.x1 - 6; x += 5 + Math.floor(r() * 9)) FAR_FOLK.push({ x, h: 4 + Math.floor(r() * 2), c: r() < 0.5 ? '#141020' : '#241a2a', walk: r() < 0.7 ? (r() < 0.5 ? 1 : -1) : 0, n: 1 + Math.floor(r() * 2), r: rankOf(i++) }); }
function drawFarFolk(t, crowd) {
  const span = MALL.x1 - MALL.x0 - 12;
  for (const f of FAR_FOLK) {
    if (f.r >= crowd) continue;
    const x = f.walk ? MALL.x0 + 6 + Math.round(frac((f.x - MALL.x0) / span + f.walk * t * f.n / LOOP) * span) : f.x;
    R(x, SHORE - 1 - f.h, 1, f.h, f.c); R(x + 1, SHORE - f.h, 1, f.h - 1, f.c);
  }
}

/* ================= Billboard on the mall's roof: a cendol stall's ad ================= */
const BB_X0 = BB.x - 2, BB_X1 = BB.x + BB.w + 1, BB_Y0 = BB.y - 2, BB_Y1 = BB.y + BB.h + 1;
const BB_LAMPS = [BB.x + 10, BB.x + BB.w - 11];
function renderBillboard() {
  const p = painter(W, H), put = p.put, DK = '#0e1018', MD = '#22283a', LT = '#3e4864';
  for (const lx of [BB.x + 8, BB.x + BB.w - 10]) for (let y = BB_Y1 + 1; y < MALL.roof; y++) { put(lx, y, LT); put(lx + 1, y, DK); }
  for (let i = BB_X0 - 2; i <= BB_X1 + 2; i++) { put(i, BB_Y1 + 1, MD); put(i, BB_Y1 + 2, DK); }
  for (let i = BB_X0; i <= BB_X1; i++) { put(i, BB_Y0, DK); put(i, BB_Y1, DK); }
  for (let y = BB_Y0; y <= BB_Y1; y++) { put(BB_X0, y, DK); put(BB_X1, y, DK); }
  for (let i = BB_X0 + 1; i < BB_X1; i++) { put(i, BB_Y0 + 1, '#2c3246'); put(i, BB_Y1 - 1, '#4a3a34'); }
  for (const lx of BB_LAMPS) {
    for (let y = BB_Y0 - 4; y < BB_Y0; y++) put(lx, y, LT);
    for (let i = lx - 1; i <= lx + 2; i++) put(i, BB_Y0 - 5, MD);
  }
  return p.done();
}
// the ad: a bowl of cendol (shaved ice, green jelly, palm sugar syrup) on a warm brown ground, CENDOL / SEJUK
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h), put = p.put;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) put(i, y, pick(['#3a1a12', '#5a2a18', '#7a3e22', '#9a5630'], 0.85 - y / h * 0.6 - Math.abs(i - 12) / 90, i, y));
  // the bowl
  for (let y = 9; y <= 15; y++) { const hw = Math.round(8 - (y - 9) * 0.8); for (let i = 11 - hw; i <= 11 + hw; i++) put(i + 1, y, y === 9 ? '#f4f8ff' : (i - 11 + hw) < 2 ? '#c8dcf4' : y === 15 ? '#5a7ab0' : '#8aaee0'); }
  for (let i = 5; i <= 18; i++) if ((i & 1) === 0) put(i, 12, '#3a68b8');
  // the ice mound, green strands, the syrup running down
  for (let y = 3; y <= 8; y++) { const hw = Math.round(Math.sqrt(Math.max(0, 25 - (8 - y) ** 2 * 1.1)) * 1.2); for (let i = 12 - hw; i <= 12 + hw; i++) put(i, y, (i + y) % 4 === 0 ? '#e2ecf6' : '#ffffff'); }
  for (const [gx, gy] of [[8, 6], [10, 7], [12, 5], [14, 7], [16, 6], [11, 8], [15, 8], [9, 8]]) { put(gx, gy, '#3ab04a'); put(gx + 1, gy, '#2a8a3a'); }
  for (const [sx, sy, n] of [[12, 3, 4], [9, 5, 3], [15, 4, 4]]) for (let k = 0; k < n; k++) put(sx + (k > 1 ? 1 : 0), sy + k, '#6a2e14');
  signText(put, 'CENDOL', 23, 4, '#fff2d8');
  signText(put, 'SEJUK', 25, 11, '#9ae8a6');
  return gradeAd(p.done(), { level: 0.8, tint: '#0d1b3a', mix: 0.1, wear: 0.15, seed: 11 });
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 6, 0.8);
  ctx.drawImage(BB_C, 0, 0);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of BB_LAMPS) { R(lx - 1, BB_Y0 - 4, 4, 1, '#f2dcaa'); halo(HL.bbLamp, lx, BB_Y0 - 3, 0.9); }
}

/* ================= Tropical drizzle at three depths, the shower, far lightning ================= */
// Each layer holds the shower's drops; the drizzle draws the first share of them, the shower (SHOWER) all.
const DRIZ = [
  { count: 230, nMin: 84, nMax: 104, len: 1, slope: 0.1, col: '#7a88b0', a: 0.34, land: [96, SHORE], lit: false },
  { count: 140, nMin: 112, nMax: 132, len: 2, slope: 0.14, col: '#a0aed2', a: 0.42, land: [KERB, 232], lit: true },
  { count: 56, nMin: 140, nMax: 160, len: 4, slope: 0.2, col: '#c6d2ec', a: 0.5, land: [H + 4, H + 4], lit: true },
];
DRIZ.forEach((L, li) => {
  const r = rng(400 + li);
  L.drops = Array.from({ length: L.count }, (_, i) => ({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() }));
});
const showerDens = t => 0.42 + 0.58 * env(t, SHOWER[0], SHOWER[1], 5);
function drawDrizzle(li, t, dens, lights) {
  const L = DRIZ[li], span = W + 30, n = Math.round(L.count * dens), len = L.len + (dens > 0.8 ? 1 : 0);
  ctx.globalAlpha = L.a;
  for (let k = 0; k < n; k++) {
    const d = L.drops[k];
    const q = t * d.n / LOOP + d.ph, fq = Math.floor(q), cyc = ((fq % d.n) + d.n) % d.n, u = q - fq;
    const yEnd = lerp(L.land[0], L.land[1], hash(d.id, cyc, 2)), y = -6 + u * (yEnd + 6);
    const x = ((hash(d.id, cyc, 1) * span - u * yEnd * L.slope) % span + span) % span - 15;
    if (li === 2 && x > 3 && x < MAMAK.x1 - 4 && y > MAMAK.ceil + 2 && y < MAMAK.floor) continue;   // under the five-foot way's roof
    let col = L.col;
    if (L.lit) for (const g of lights) { const dx = (x - g.x) / g.rx, dy = (y - g.y) / g.ry; if (dx * dx + dy * dy < 1) { col = g.col; break; } }
    ctx.fillStyle = col;
    for (let j = 0; j < len; j++) ctx.fillRect(Math.round(x + j * L.slope), Math.round(y + j), 1, 1);
  }
  ctx.globalAlpha = 1;
}
// a soft swell of light behind Bukit Nanas for each bolt, and a thin far fork at its peak (city's own weather only,
// never with reduced motion)
function boltLight(t) {
  let best = null;
  for (const b of BOLTS) { const v = smooth(b.t - 0.15, b.t + 0.1, t) * (1 - smooth(b.t + 0.3, b.t + 1.9, t)); if (v > 0 && (!best || v > best.v)) best = { ...b, v }; }
  return best;
}
const FORK = [];
{ const r = rng(13); for (const b of BOLTS) { let x = b.x, pts = [[x, 64]]; for (let y = 64; y < 138; y += 4 + Math.floor(r() * 4)) { x += Math.round((r() - 0.5) * 6); pts.push([x, y]); } FORK.push(pts); } }
function drawBolt(t, B) {
  halo(HL.flash, B.x, 112, B.v);
  halo(HL.flashCore, B.x, 96, B.v * 0.8);
  if (B.v > 0.75) {
    const pts = FORK[BOLTS.findIndex(b => b.t === B.t)];
    ctx.globalAlpha = (B.v - 0.75) * 3; ctx.fillStyle = '#eef0ff';
    for (let k = 1; k < pts.length; k++) lineP((x, y) => ctx.fillRect(x, y, 1, 1), pts[k - 1][0], pts[k - 1][1], pts[k][0], pts[k][1]);
    ctx.globalAlpha = 1;
  }
}
// a plane crossing high: its strobe and its red and green lamps
function drawPlanes(t) {
  for (const P of PLANES) {
    if (t < P.a || t >= P.b) continue;
    const u = (t - P.a) / (P.b - P.a), x = Math.round(lerp(P.x0, P.x1, u)), y = Math.round(lerp(P.y0, P.y1, u));
    px(ctx, x, y, '#6a7088');
    if (frac(t * 1.2) < 0.12) { px(ctx, x, y, '#ffffff'); halo(HL.white, x, y, 0.8); }
    px(ctx, x - 1, y, frac(t) < 0.5 ? '#ff4a4a' : '#5a2020'); px(ctx, x + 1, y, '#4aff6a');
  }
}
// a picked weather (site/js/engine/weather.js): Kuala Lumpur draws its own drizzle (with its shower and far lightning
// only in its own weather); the rest land on the far shore, the lakeside path and past the bottom, catch the park
// lamps and the mamak's tubes, and keep out of the five-foot way
const WEATHER = weatherPlans(['drizzle']);
const WX_LAND = [[120, SHORE], [KERB, 232], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: LAMPS[0].x, y: 196, rx: 26, ry: 22, col: '#ffd8a0' }, { x: LAMPS[1].x, y: 196, rx: 26, ry: 22, col: '#ffd8a0' },
  { x: 56, y: 206, rx: 70, ry: 30, col: '#e8fff4' }, { x: BOLLARDS[0] + 1, y: 236, rx: 14, ry: 10, col: '#ffd8a0' }, { x: BOLLARDS[1] + 1, y: 236, rx: 14, ry: 10, col: '#ffd8a0' },
];
const WX_LIGHTS_BB = [...WX_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y - 2, rx: 28, ry: 8, col: '#ffe0b0' }];
const SHELTER = [[], [], [[4, MAMAK.ceil + 3, MAMAK.x1 - 5, MAMAK.floor - 1]]];   // the five-foot way's roof keeps the near drops off the shop
const WX = { land: WX_LAND, lights: WX_LIGHTS, shelter: SHELTER };
const WX_BB = { land: WX_LAND, lights: WX_LIGHTS_BB, shelter: SHELTER };

/* ================= Pre-render everything static ================= */
function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const d = Math.hypot((i - 240) / 300, (y - 140) / 190);
    const a = clamp((d - 0.66) * 1.4, 0, 0.5) + (y > 256 ? (y - 256) / 14 * 0.2 : 0);
    if (a > 0.02 && a > bayer(i, y) * 0.6) p.put(i, y, '#02040a', Math.round(a * 255));
  }
  return p.done();
}
let HL, SKY_C, MOON_C, WISP_C, FAR_C, MKL_C, HILL_C, MIDS_C, TOWERS_C, MALL_C, SHORE_C, WATER_C, PATH_C, PLANT_C, TRUNK_C, CANOPY_C,
  MAMAK_C, GRID_C, TABLES_C, NEAR_C, BB_C, BB_AD, PITCH, VIG_C;
function prerender() {
  HL = {
    moon: makeHalo(40, '#8eaaff', 0.2, 1.8), moonCore: makeHalo(14, '#fff2d8', 0.32, 1.5), city: makeHalo(170, '#ff9a5a', 0.07, 1.3),
    crown: makeHalo(34, '#e6ecff', 0.15, 1.6), shaft: makeHalo(56, '#c8d4ff', 0.07, 1.5), ball: makeHalo(7, '#ffffff', 0.55, 1.3),
    lantern: makeHalo(14, '#fff2d0', 0.32, 1.5), pod: makeHalo(14, '#5ab0ff', 0.28, 1.5), red: makeHalo(4, '#ff3030', 0.6, 1), white: makeHalo(5, '#ffffff', 0.5, 1),
    atrium: makeHalo(30, '#ffc890', 0.12, 1.5), tube: makeHalo(16, '#e8fff4', 0.2, 1.5), shop: makeHalo(72, '#d8ffe8', 0.09, 1.5),
    tea: makeHalo(18, '#ffb060', 0.16), lamp: makeHalo(40, '#ffb060', 0.2, 1.7), lampCore: makeHalo(10, '#ffe2a8', 0.5, 1.4),
    bbLamp: makeHalo(6, '#ffc878', 0.3, 1.6), bbWash: makeHalo(34, '#ffb870', 0.07, 1.5), tv: makeHalo(10, '#5aff8a', 0.18, 1.4),
    win: makeHalo(10, '#ffb45a', 0.12), bollard: makeHalo(12, '#ffc070', 0.3, 1.5), signLamp: makeHalo(9, '#ffe8b0', 0.25, 1.5),
    flash: makeHalo(110, '#b8b4ff', 0.3, 1.4), flashCore: makeHalo(40, '#e8e8ff', 0.3, 1.4),
    jet_white: makeHalo(80, '#c8dcff', 0.12, 1.5), jet_warm: makeHalo(80, '#ffd8a0', 0.12, 1.5), jet_cyan: makeHalo(80, '#6ad0ff', 0.14, 1.5),
    jet_rose: makeHalo(80, '#ff7ac8', 0.13, 1.5), jet_violet: makeHalo(80, '#9a7aff', 0.14, 1.5),
  };
  for (const k of Object.keys(MKL_TINT_HALO)) HL['pod_' + k] = makeHalo(16, MKL_TINT_HALO[k], 0.3, 1.5);
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(renderWisp);
  FAR_C = renderFar(); MKL_C = [null, ...WASH_TINTS].map(renderMKL); HILL_C = renderHill(); MIDS_C = renderMids();
  TOWERS_C = renderTowers(); MALL_C = renderMall();
  SHORE_C = renderShoreTrees(); WATER_C = renderWater(); PATH_C = renderPath(); PLANT_C = renderPlants();
  TRUNK_C = renderTrunk(); CANOPY_C = renderCanopy();
  MAMAK_C = renderMamak(); GRID_C = renderGriddle(); TABLES_C = renderTables(); NEAR_C = renderNear();
  BB_C = renderBillboard(); BB_AD = renderBillboardAd(); PITCH = makePitch(BB.w, BB.h, { accent: '#14a0ff' });
  VIG_C = renderVignette();
}
const MKL_TINT_HALO = { 0: '#5ab0ff', 1: '#3aff8a', 2: '#3a8aff', 3: '#a05aff', 4: '#ff5ab4' };

/* ================= Frame compositor (back to front) ================= */
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, own = !weather || weather === 'city';
  const dens = own ? showerDens(t) : 0.42, lights = billboard ? WX_LIGHTS_BB : WX_LIGHTS;
  const bolt = own && wx.drizzle && !reducedMotion ? boltLight(t) : null;
  // sky, stars, moon, wisps, a plane, the far lightning
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e6ecff';
  const veil = own ? 1 - 0.55 * env(t, SHOWER[0], SHOWER[1], 6) : 1;       // the shower's cloud veils the moon and stars
  for (const s of STARS) { ctx.globalAlpha = veil * s.b * (0.6 + 0.4 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  for (const w of WISP_C) drawScroll(w.c, w.y, w.k, t);
  halo(HL.moon, MOON.x, MOON.y, veil); halo(HL.moonCore, MOON.x, MOON.y, veil);
  ctx.globalAlpha = 0.35 + 0.65 * veil; ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r); ctx.globalAlpha = 1;
  drawPlanes(t);
  if (bolt) drawBolt(t, bolt);
  halo(HL.city, 254, 196);
  // the far city, Menara KL (its own look, or the colour wash) and Bukit Nanas
  ctx.drawImage(FAR_C, 0, 0);
  for (const w of WINS_FAR) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < 0.5) px(ctx, w.x, w.y, w.col);
  for (const a of AVI) { const on = frac(t / a.per + a.ph) < 0.35; px(ctx, a.x, a.y, on ? '#ff3a3a' : '#4a1418'); if (on) halo(HL.red, a.x, a.y, 0.8); }
  const [ma, mb, mu] = mklMix(t);
  ctx.drawImage(MKL_C[ma], 0, 0);
  if (mu > 0) { ctx.globalAlpha = mu; ctx.drawImage(MKL_C[mb], 0, 0); ctx.globalAlpha = 1; }
  halo(HL['pod_' + ma], MKL.x, 67, 0.9 * (1 - mu)); if (mu > 0) halo(HL['pod_' + mb], MKL.x, 67, 0.9 * mu);
  const mt = frac(t / 2) < 0.4; px(ctx, MKL.x, MKL.tip, mt ? '#ff4a3a' : '#6a1a18'); if (mt) halo(HL.red, MKL.x, MKL.tip, 1);
  ctx.drawImage(HILL_C, 0, 0);
  if (wx.drizzle) drawDrizzle(0, t, dens, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the towers round the twins, their windows switching on and off
  ctx.drawImage(MIDS_C, 0, 0);
  for (const w of WINS) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < 0.55) px(ctx, w.x, w.y, w.col);
  // the Petronas Twin Towers: a slow breath on the crowns, their windows' sparkle
  const breath = 0.9 + 0.1 * wave(t, 12);
  for (const cx of [TOW.l, TOW.r]) { halo(HL.shaft, cx, 120, 0.9); halo(HL.crown, cx, 52, 0.95 * breath); }
  ctx.drawImage(TOWERS_C, 0, 0);
  for (const s of SPARKS) if (wave(t, s.k * 4, s.ph) > 0.2) px(ctx, s.x, s.y, s.col);
  for (const cx of [TOW.l, TOW.r]) { halo(HL.lantern, cx, CROWN_TOP + 1, 0.6 * breath); halo(HL.ball, cx, BALL[0] + 1, 0.55); }
  // Suria KLCC's lake front and its people, the far shore's trees and palms, the billboard on the roof
  ctx.drawImage(MALL_C, 0, 0);
  halo(HL.atrium, 254, 158, 0.9);
  drawCityLife(t, crowd);
  drawFarFolk(t, crowd);
  ctx.drawImage(SHORE_C, 0, 0);
  if (billboard) drawBillboard(t, pitch);
  // Lake Symphony: the mirror, the towers' silver and the mall's warm streaks, the fountain line
  ctx.drawImage(WATER_C, 0, SHORE);
  reflect(ctx, t, { top: SHORE, rows: KERB - SHORE, squash: 1.6, alpha: 0.5 });
  for (const cx of [TOW.l, TOW.r]) streak(ctx, t, cx, 7, '#d4dcee', 0.32, cx, SHORE + 12, KERB);
  streak(ctx, t, 254, 10, '#ffc880', 0.3, 7, SHORE + 1, KERB);
  drawFountain(t);
  // the lakeside path, the track and its lamps, the rain tree
  ctx.drawImage(PATH_C, 0, 0);
  for (const L of LAMPS) { halo(HL.lamp, L.x, L.y, 0.9); halo(HL.lampCore, L.x, L.y, 0.9); }
  if (wx.drizzle) drawDrizzle(1, t, dens, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  ctx.drawImage(TRUNK_C, 0, 0);
  drawCanopy(t);
  // the mamak: its building and life upstairs, the shop's people, then those on the path
  ctx.drawImage(MAMAK_C, 0, 0);
  drawMamakUpper(t, crowd);
  drawMamakLights(t);
  halo(HL.shop, 56, 186, 0.9);
  for (const [a, b] of TUBES) halo(HL.tube, (a + b) >> 1, MAMAK.ceil + 3, 0.9);
  halo(HL.tv, TV.x + (TV.w >> 1), TV.y + 4, 0.8);
  halo(HL.tea, URNS[0] + 4, 176, 0.8);
  drawShopPeople(t, crowd);
  drawPathPeople(t, crowd);
  // the wet stone mirrors the shop and the path's people, dimly; the puddles more clearly; streaks under the lamps
  reflect(ctx, t, { top: FEET + 2, rows: 40, squash: 1.1, alpha: 0.2, k1: 37, k2: 89, amp: 0.4 });
  drawPuddles(t, wx.drizzle ? dens : 0);
  if (wx.drizzle) drawRings(t, dens);
  for (const L of LAMPS) streak(ctx, t, L.x, 4, '#ffc070', 0.28, L.x, FEET + 3, 232);
  for (const bx of BOLLARDS) halo(HL.bollard, bx + 1, trackTop(bx) - 9, 0.9);
  // the diners out on the path, the joggers on the track, the nearest table, the planting along the front
  drawDinersBehind(t, crowd);
  ctx.drawImage(TABLES_C, 0, 0);
  drawDinersFront(t, crowd);
  drawCat(t);
  drawRider(t, crowd);
  drawJoggers(t, crowd);
  ctx.drawImage(NEAR_C, 0, 0);
  drawSteam(t);
  drawPlants(t);
  // weather in front of everything
  if (wx.drizzle) drawDrizzle(2, t, dens, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'kuala-lumpur',
  name: 'Kuala Lumpur',
  tagline: 'Twin towers over Lake Symphony',
  timeZone: 'Asia/Kuala_Lumpur',
  accent: '#14a0ff',
  focusX: (TOW.l + TOW.r) >> 1,
  landmark: [TOW.l - TOW.hw, TOW.r + TOW.hw],         // the Petronas Twin Towers
  landmarkTop: TOW.tip,                                // the spires' tips
  billboard: BB,
  crowd: CROWD,
  spots: [                                             // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: TOW.l - TOW.hw, y: TOW.tip, w: TOW.r - TOW.l + 2 * TOW.hw + 1, h: MALL.atrium - TOW.tip - 1 },   // the twin towers above the mall
    { kind: 'water', x: 228, y: SHORE + 1, w: 56, h: 7 },                                                    // Lake Symphony under the towers
    { kind: 'neon', x: MAMAK.x1 - 31, y: MAMAK.sign + 1, w: 29, h: 9 },                                                     // the mamak's 24 JAM
    { kind: 'cat', x: 49, y: 119, w: 10, h: 14 },                                                                           // the cat on the air conditioner
  ],
  ambience: { rain: 0.32, water: 0.4, crowd: 0.25, traffic: 0.2, crickets: 0.22 },
  events: [{ t: 74, type: 'splash', x: 254, gain: 0.6 }, { t: 124, type: 'chime', x: 86, gain: 0.5 }, { t: 188, type: 'splash', x: 254, gain: 0.5 },
    { t: 210.5, type: 'thunder', x: 132, gain: 0.45 }, { t: 223.4, type: 'thunder', x: 196, gain: 0.4 }],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
